import React, { useState, useEffect, useContext } from "react";
import { Table, Spin, Alert, Input, Button, Modal, Form, Select, message, Space, Checkbox, Divider, Tabs, DatePicker } from "antd";
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useSelector } from "react-redux";
import { RoleContext } from "context/RoleContext";
import {
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  TrophyOutlined,
  DollarOutlined,
  HomeOutlined,
  EditOutlined,
  DeleteOutlined,
  TagsOutlined,
  ArrowLeftOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import PageInfo from "shared/Components/pageInfo";
import { avatar } from "helper/images";
import { pendingPayment, successPayment } from "helper/icons";
import { client } from "helper/apolloConfig";
import Dropdown from "shared/Components/dropdown";
import moment from "moment";

const { Search } = Input;

// GraphQL Queries
const GET_ALL_MEMBERS = gql`
  query GetAllRegisteredMember($pageNumber: Int!, $pageSize: Int!, $search: String) {
    getAllRegisteredMember(pageNumber: $pageNumber, pageSize: $pageSize, search: $search) {
      pageNumber
      pageSize
      totalDocs
      totalPages
      totalCount
      data {
        id
        name
        email
        location_address
        phone_number
        emergency_contact_name
        emergency_contact_number
        profile
        authId
        role
        city
        zipCode
        state
        website
        distance_travel
        notes
        mark_as_paid
        join_date
        createdAt
        judgeStatus
        isBlocked
      }
    }
  }
`;

const GET_MEMBER_BY_AUTH_ID = gql`
  query GetCommonUserDetail($authId: String!) {
    getCommonUserDetail(authId: $authId) {
      id
      name
      email
      location_address
      phone_number
      emergency_contact_name
      emergency_contact_number
      profile
      authId
      role
      city
      zipCode
      state
      website
      distance_travel
      notes
      mark_as_paid
      createdAt
      noOfTrialsJudged
      noOfTrialsParticipated
      noofTrialsHosted
      noOfUpcomingTrials
      isBlocked
      level
      timezone
    }
  }
`;

const GET_ALL_DOGS = gql`
  query GetAllRegisteredDogs($pageNumber: Int!, $pageSize: Int!, $search: String) {
    getAllRegisteredDogs(pageNumber: $pageNumber, pageSize: $pageSize, search: $search) {
      pageNumber
      pageSize
      totalDocs
      totalPages
      totalCount
      data {
        id
        dog_id
        call_name
        registration_name
        breed
        breed_mix
        gender
        authId
        owner_name
        dogImage
        rescue
        date_of_birth
        titleDate
        title
        qs
        dogLegs
        status
        payment_status
        notes
      }
    }
  }
`;

const GET_DOG_SIT_BY_ID = gql`
  query GetRegisteredDogSITById($dogId: Int!) {
    getRegisteredDogSITById(dogId: $dogId) {
      id
      birch
      anise
      clove
    }
  }
`;

const GET_ALL_TRIAL_RESULTS = gql`
  query GetAllTrialResult($pageNumber: Int!, $pageSize: Int!) {
    getAllTrialResult(pageNumber: $pageNumber, pageSize: $pageSize) {
      pageNumber
      pageSize
      totalDocs
      totalPages
      totalCount
      data {
        id
        date
        search
        handler
        dog
        breed
        div
        qNq
        time
        points
        place
        trialCalendarId
      }
    }
  }
`;

const GET_ALL_HOSTS = gql`
  query GetAllHost($pageNumber: Int!, $pageSize: Int!, $search: String) {
    getAllHost(pageNumber: $pageNumber, pageSize: $pageSize, search: $search) {
      pageNumber
      pageSize
      totalDocs
      totalPages
      totalCount
      data {
        phone_number
        name
        email
        location_address
        authId
        hostStatus
        city
        state
        zipCode
      }
    }
  }
`;

const GET_ALL_USERS = gql`
  query GetAllUsers {
    getAllUsers {
      id
      username
      email
      role
      city
      state
      zipCode
      timezone
      createdAt
      updatedAt
    }
  }
`;

const GET_ALL_PAYMENTS = gql`
  query GetAllRegisteredDogsByAdmin($pageNumber: Int!, $pageSize: Int!, $search: String) {
    getAllRegisteredDogsByAdmin(pageNumber: $pageNumber, pageSize: $pageSize, search: $search) {
      pageNumber
      pageSize
      totalDocs
      totalPages
      totalCount
      data {
        id
        call_name
        registration_name
        owner_name
        email
        phone_number
        dateOfJoining
        status
        amount
        payment_status
        authId
      }
    }
  }
`;

const UPDATE_PAYMENT_STATUS = gql`
  mutation UpdateRegisteredDogMarkPaid($authId: String!, $data: UpdateDogMarkPaidByAdminInput!) {
    updateRegisteredDogMarkPaid(dogId: $authId, data: $data) {
      message
    }
  }
`;

const GET_ALL_TRIALS = gql`
  query GetAllTrialCalendar($pageNumber: Int!, $pageSize: Int!, $search: String, $approvalStatus: String, $role: String) {
    getAllTrialCalendar(pageNumber: $pageNumber, pageSize: $pageSize, search: $search, approvalStatus: $approvalStatus, role: $role) {
      pageNumber
      pageSize
      totalDocs
      totalPages
      totalCount
      data {
        id
        authId
        host {
          id
          hostName
          contactAddress
          city
          trialSecretaryName
          state
          trialSecretaryEmail
          zip
          phoneNumber
          paypalEmail
          paypalPaymentLink
        }
        trial {
          id
          trialName
          trialLocationName
          trialLocationAddress
          city
          state
          zip
          timezone
          trialDate
          openDate
          closeDate
          cutoffDate
          trialFile
          description
          notes
          status
          approvalStatus
          noOfJudge
          trialFees
          maxEntries
          trialImages
          judgeAuthId
        }
        search {
          id
          level
          element
          searchName
          entryFee
          entryCount
        }
      }
    }
  }
`;

const UPDATE_TRIAL_BY_ADMIN = gql`
  mutation UpdateHostOwnTrialCalendar($trialCalendarId: Int!, $data: UpdateTrialCalendarInput!) {
    updateHostOwnTrialCalendar(trialCalendarId: $trialCalendarId, data: $data) {
      id
      authId
      host {
        id
        hostName
        contactAddress
        city
        trialSecretaryName
        state
        trialSecretaryEmail
        zip
        phoneNumber
        paypalEmail
        paypalPaymentLink
      }
      trail {
        id
        trialName
        trialLocationName
        trialLocationAddress
        city
        state
        zip
        timezone
        trialDate
        openDate
        closeDate
        cutoffDate
        trialFile
        description
        notes
        status
        approvalStatus
        noOfJudge
        trialFees
        maxEntries
        trialImages
        judgeAuthId
      }
      searches {
        id
        level
        element
        searchName
        entryFee
      }
    }
  }
`;

const UPDATE_TRIAL_APPROVAL_STATUS = gql`
  mutation UpdateTrialApprovalStatus($id: Int!, $approvalStatus: String!) {
    updateTrialApprovalStatus(id: $id, data: { approvalStatus: $approvalStatus }) {
      message
    }
  }
`;

const DELETE_TRIAL_BY_ADMIN = gql`
  mutation DeleteTrialCalendarByAdmin($trialCalendarId: Int!) {
    deleteTrialCalendarByAdmin(trialCalendarId: $trialCalendarId) {
      message
    }
  }
`;

const GET_TRIAL_BY_ID = gql`
  query GetTrialCalendarById($trialCalendarId: Int!) {
    getTrialCalendarById(trialCalendarId: $trialCalendarId) {
      id
      authId
      judgeName
      hostName
      host {
        id
        hostName
        contactAddress
        city
        trialSecretaryName
        state
        trialSecretaryEmail
        zip
        phoneNumber
        paypalEmail
        paypalPaymentLink
      }
      trail {
        id
        trialName
        trialLocationName
        trialLocationAddress
        city
        state
        zip
        timezone
        trialDate
        openDate
        closeDate
        cutoffDate
        trialFile
        description
        notes
        status
        approvalStatus
        noOfJudge
        trialFees
        maxEntries
        trialImages
        judgeAuthId
      }
      searches {
        id
        level
        element
        searchName
        entryFee
        entryCount
      }
    }
  }
`;

const GET_DOG_ENTRIES_BY_TRIAL = gql`
  query GetRegisteredDogsTrialByTrialCalendar($trialCalendarId: Int!, $pageNumber: Int!, $pageSize: Int!, $search: String) {
    getRegisteredDogsTrialByTrialCalendar(trialCalendarId: $trialCalendarId, pageNumber: $pageNumber, pageSize: $pageSize, search: $search) {
      data {
        id
        dog
        division
        elements
        authId
        trialCalendarId
        memberName
        email
        address
        phoneNumber
        emergencyContactName
        emergencyContactNumber
      }
      pageNumber
      pageSize
      totalDocs
      totalPages
      totalCount
    }
  }
`;

// GraphQL Mutations
const UPDATE_USER_BY_ADMIN = gql`
  mutation UpdateUserByAdmin($authId: String!, $data: UpdateUserByAdminInput!) {
    updateUserByAdmin(authId: $authId, data: $data) {
      message
    }
  }
`;

const UPDATE_DOG_BY_ADMIN = gql`
  mutation UpdateRegisteredDog($id: Int!, $data: UpdateDogByAdminInput!) {
    updateRegisteredDog(id: $id, data: $data) {
      message
    }
  }
`;

const UPDATE_DOG_SIT = gql`
  mutation UpdateRegisteredDogSITStatus($id: Int!, $data: UpdateDogSitInput!) {
    updateRegisteredDogSITStatus(id: $id, data: $data) {
      message
    }
  }
`;

const DELETE_DOG = gql`
  mutation DeleteRegisteredDogs($id: Int!) {
    deleteRegisteredDogs(id: $id) {
      message
    }
  }
`;

const UPDATE_MEMBER_PROFILE = gql`
  mutation UpdateMyMemberProfile($data: UpdateMemberInput!) {
    updateMyMemberProfile(data: $data) {
      id
      first_name
      last_name
      email
    }
  }
`;

const DEACTIVATE_USER = gql`
  mutation DeactivateUser($authId: String!) {
    deactivateUser(authId: $authId) {
      message
    }
  }
`;

const REACTIVATE_USER = gql`
  mutation ReactivateUser($authId: String!) {
    reactivateUser(authId: $authId) {
      message
    }
  }
`;

const UPDATE_USER_ROLES = gql`
  mutation UpdateUserRoles($authId: String!, $role: [String!]!) {
    updateUserRoles(authId: $authId, role: $role) {
      message
    }
  }
`;

const DELETE_MEMBER_PERMANENTLY = gql`
  mutation DeleteUserPermanently($authId: String!) {
    deleteUserPermanently(authId: $authId) {
      message
    }
  }
`;

const PERMANENT_DELETE_PASSWORD = 'RosanaD';

// Members List Component
const MembersList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletingMember, setDeletingMember] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [permDeleteModalVisible, setPermDeleteModalVisible] = useState(false);
  const [permDeletingMember, setPermDeletingMember] = useState(null);
  const [permDeletePassword, setPermDeletePassword] = useState('');
  const [form] = Form.useForm();

  // Fetch ALL members at once
  const { loading, error, data, refetch } = useQuery(GET_ALL_MEMBERS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null },
  });

  const [updateUser] = useMutation(UPDATE_USER_BY_ADMIN, {
    onCompleted: () => {
      message.success('Member updated successfully!');
      setEditModalVisible(false);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to update member: ${err.message}`);
    },
  });

  const [updateUserRoles] = useMutation(UPDATE_USER_ROLES, {
    onCompleted: () => {
      message.success('Roles and Auth0 metadata updated successfully!');
    },
    onError: (err) => {
      message.error(`Failed to update roles: ${err.message}`);
    },
  });

  const [deactivateUser, { loading: deleteLoading }] = useMutation(DEACTIVATE_USER, {
    onCompleted: () => {
      message.success('Member deactivated successfully! All associated data has been blocked.');
      setDeleteModalVisible(false);
      setConfirmationText('');
      setDeletingMember(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to deactivate member: ${err.message}`);
    },
  });

  const [reactivateUser, { loading: reactivateLoading }] = useMutation(REACTIVATE_USER, {
    onCompleted: () => {
      message.success('Member reactivated successfully! User can now access the system.');
      setDeleteModalVisible(false);
      setConfirmationText('');
      setDeletingMember(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to reactivate member: ${err.message}`);
    },
  });

  const [permanentlyDeleteMember, { loading: permDeleteLoading }] = useMutation(DELETE_MEMBER_PERMANENTLY, {
    onCompleted: () => {
      message.success('Member permanently deleted from database and Auth0.');
      setPermDeleteModalVisible(false);
      setPermDeletingMember(null);
      setPermDeletePassword('');
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to permanently delete member: ${err.message}`);
    },
  });

  const handlePermDelete = (record) => {
    setPermDeletingMember(record);
    setPermDeletePassword('');
    setPermDeleteModalVisible(true);
  };

  const handleConfirmPermDelete = async () => {
    if (permDeletePassword !== PERMANENT_DELETE_PASSWORD) {
      message.error('Incorrect password. Deletion cancelled.');
      return;
    }
    try {
      await permanentlyDeleteMember({ variables: { authId: permDeletingMember.authId } });
    } catch (err) {
      console.error('Permanent delete failed:', err);
    }
  };

  // Sort by ID ascending
  const sortedData = data?.getAllRegisteredMember?.data
    ? [...data.getAllRegisteredMember.data].sort((a, b) => a.id - b.id)
    : [];

  // Client-side filtering based on search term
  const filteredData = sortedData.filter(member => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      member.name?.toLowerCase().includes(searchLower) ||
      member.email?.toLowerCase().includes(searchLower) ||
      member.phone_number?.toLowerCase().includes(searchLower)
    );
  });

  const handleEdit = (record) => {
    setEditingMember(record);
    const nameParts = record.name?.split(' ') || ['', ''];
    form.setFieldsValue({
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      phone_number: record.phone_number || '',
      location_address: record.location_address || '',
      city: record.city || '',
      state: record.state || '',
      zipCode: record.zipCode || '',
      emergency_contact_name: record.emergency_contact_name || '',
      emergency_contact_number: record.emergency_contact_number || '',
      website: record.website || '',
      distance_travel: record.distance_travel || '',
      notes: record.notes || '',
      role: record.role || [],
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      // Get original roles to check if they changed (create copies to avoid mutating read-only arrays)
      const originalRoles = [...(editingMember.role || [])];
      const newRoles = [...(values.role || [])];
      const rolesChanged = JSON.stringify(originalRoles.sort()) !== JSON.stringify(newRoles.sort());

      // If roles changed, call updateUserRoles (updates Auth0 metadata and judgeStatus)
      if (rolesChanged) {
        await updateUserRoles({
          variables: {
            authId: editingMember.authId,
            role: values.role,  // Use original values.role, not sorted copy
          },
        });
      }

      // Remove mark_as_paid from the data since it's not in UpdateUserByAdminInput
      const { mark_as_paid, ...updateData } = values;

      // Always update all other fields using updateUserByAdmin
      await updateUser({
        variables: {
          authId: editingMember.authId,
          data: updateData,
        },
      });
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const handleDelete = (record) => {
    setDeletingMember(record);
    setConfirmationText('');
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmationText !== deletingMember?.name) {
      message.error('Member name does not match. Please type the exact name to confirm.');
      return;
    }

    try {
      // Check if member is currently blocked - if so, reactivate; otherwise deactivate
      if (deletingMember.isBlocked) {
        await reactivateUser({
          variables: {
            authId: deletingMember.authId,
          },
        });
      } else {
        await deactivateUser({
          variables: {
            authId: deletingMember.authId,
          },
        });
      }
    } catch (err) {
      console.error('Deactivate/Reactivate failed:', err);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone_number', key: 'phone_number' },
    { title: 'City', dataIndex: 'city', key: 'city' },
    { title: 'State', dataIndex: 'state', key: 'state' },
    {
      title: 'Join Date',
      dataIndex: 'join_date',
      key: 'join_date',
      width: 120,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A'
    },
    { title: 'Roles', dataIndex: 'role', key: 'role', render: (roles) => roles?.join(', ') || 'N/A' },
    {
      title: 'Judge Status',
      dataIndex: 'judgeStatus',
      key: 'judgeStatus',
      render: (status) => {
        if (!status) return 'N/A';
        // Capitalize first letter
        return status.charAt(0).toUpperCase() + status.slice(1);
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            size="small"
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: '#fff',
              opacity: 1,
              visibility: 'visible'
            }}
          >
            Edit
          </Button>
          <Button
            danger={!record.isBlocked}
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record);
            }}
            size="small"
            style={{
              backgroundColor: record.isBlocked ? '#52c41a' : undefined,
              borderColor: record.isBlocked ? '#52c41a' : undefined,
              color: record.isBlocked ? '#fff' : undefined,
              opacity: 1,
              visibility: 'visible'
            }}
          >
            {record.isBlocked ? 'Reactivate' : 'Deactivate'}
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handlePermDelete(record);
            }}
            size="small"
            style={{ opacity: 1, visibility: 'visible', fontWeight: 'bold' }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title="Database Admin - Members" />
      <div className="flex flex-col gap-4 mt-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Members ({filteredData.length} total)
          </h2>
        </div>
        <Search
          placeholder="Search members by name, email, phone..."
          allowClear
          enterButton={
            <Button
              type="primary"
              style={{
                backgroundColor: '#5A1E66',
                borderColor: '#5A1E66',
                color: '#fff'
              }}
            >
              Search
            </Button>
          }
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>
      <div className="mt-6">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={false}
          loading={loading}
          className="border border-purple-100 rounded-lg"
          onRow={(record) => ({
            onClick: () => navigate(`/admin/database/members/${encodeURIComponent(record.authId)}`),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      {/* Edit Member Modal */}
      <Modal
        title="Edit Member"
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        okText="Save"
        okButtonProps={{
          type: 'primary',
          style: {
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }
        }}
      >
        <Form form={form} layout="vertical">
          {/* Personal Information */}
          <Divider orientation="left" style={{ marginTop: 0 }}>Personal Information</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="first_name"
              label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="last_name"
              label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item name="phone_number" label="Phone Number">
            <Input />
          </Form.Item>

          {/* Address Information */}
          <Divider orientation="left">Address Information</Divider>
          <Form.Item name="location_address" label="Street Address">
            <Input />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <Form.Item name="city" label="City">
              <Input />
            </Form.Item>
            <Form.Item name="state" label="State">
              <Input />
            </Form.Item>
            <Form.Item name="zipCode" label="ZIP Code">
              <Input />
            </Form.Item>
          </div>

          {/* Emergency Contact */}
          <Divider orientation="left">Emergency Contact</Divider>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item name="emergency_contact_name" label="Emergency Contact Name">
              <Input />
            </Form.Item>
            <Form.Item name="emergency_contact_number" label="Emergency Contact Phone">
              <Input />
            </Form.Item>
          </div>

          {/* Additional Information */}
          <Divider orientation="left">Additional Information</Divider>
          <Form.Item name="website" label="Website">
            <Input placeholder="https://" />
          </Form.Item>
          <Form.Item name="distance_travel" label="Distance Willing to Travel">
            <Input placeholder="e.g., 100 miles, 2 hours" />
          </Form.Item>

          {/* Roles & Admin */}
          <Divider orientation="left">Roles & Admin</Divider>
          <Form.Item name="role" label="Roles">
            <Select
              mode="multiple"
              placeholder="Select roles"
              onSelect={() => {
                setTimeout(() => {
                  document.activeElement?.blur();
                }, 100);
              }}
            >
              <Select.Option value="Member">Member</Select.Option>
              <Select.Option value="Judge">Judge</Select.Option>
              <Select.Option value="Host">Host</Select.Option>
              <Select.Option value="Super Admin">Super Admin</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Admin Notes">
            <Input.TextArea rows={3} placeholder="Internal notes about this member..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Deactivate/Reactivate Member Confirmation Modal */}
      <Modal
        title={deletingMember?.isBlocked ? "Reactivate Member" : "Deactivate Member"}
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setConfirmationText('');
          setDeletingMember(null);
        }}
        width={600}
        okText={deletingMember?.isBlocked ? "Reactivate" : "Deactivate"}
        okButtonProps={{
          danger: !deletingMember?.isBlocked,
          disabled: confirmationText !== deletingMember?.name,
          loading: deletingMember?.isBlocked ? reactivateLoading : deleteLoading,
          style: {
            backgroundColor: confirmationText === deletingMember?.name
              ? (deletingMember?.isBlocked ? '#52c41a' : '#ff4d4f')
              : undefined,
            borderColor: confirmationText === deletingMember?.name
              ? (deletingMember?.isBlocked ? '#52c41a' : '#ff4d4f')
              : undefined,
            color: confirmationText === deletingMember?.name ? '#fff' : undefined
          }
        }}
        cancelText="Cancel"
      >
        <Alert
          message={deletingMember?.isBlocked
            ? "This will reactivate the member and unblock their Auth0 account"
            : "Warning: This will deactivate the member and block their Auth0 account"}
          description={deletingMember?.isBlocked
            ? "The member will be able to access the system again. This action affects their member profile and Auth0 account."
            : "The member will be blocked and unable to access the system. This action affects their member profile and Auth0 account."}
          type={deletingMember?.isBlocked ? "info" : "warning"}
          showIcon
          style={{ marginBottom: '20px' }}
        />
        <p style={{ marginBottom: '10px' }}>
          <strong>Member to {deletingMember?.isBlocked ? 'reactivate' : 'deactivate'}:</strong> {deletingMember?.name} ({deletingMember?.email})
        </p>
        <p style={{ marginBottom: '10px' }}>
          To confirm, please type the member's full name: <strong>{deletingMember?.name}</strong>
        </p>
        <Input
          placeholder={`Type "${deletingMember?.name}" to confirm`}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          onPressEnter={() => {
            if (confirmationText === deletingMember?.name) {
              handleConfirmDelete();
            }
          }}
        />
      </Modal>

      {/* Permanently Delete Member Modal */}
      <Modal
        title={<span style={{ color: '#cf1322' }}>⚠️ Permanently Delete Member</span>}
        open={permDeleteModalVisible}
        onOk={handleConfirmPermDelete}
        onCancel={() => {
          setPermDeleteModalVisible(false);
          setPermDeletingMember(null);
          setPermDeletePassword('');
        }}
        width={580}
        okText="Permanently Delete"
        okButtonProps={{
          danger: true,
          disabled: permDeletePassword !== PERMANENT_DELETE_PASSWORD,
          loading: permDeleteLoading,
        }}
        cancelText="Cancel"
      >
        <Alert
          message="This action is PERMANENT and cannot be undone"
          description="This will delete the member's account, all their dogs, trial history, results, and their Auth0 login — forever. There is no recovery."
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
        <p style={{ marginBottom: '8px' }}>
          <strong>Member to delete:</strong> {permDeletingMember?.name}
        </p>
        <p style={{ marginBottom: '8px' }}>
          <strong>Email:</strong> {permDeletingMember?.email}
        </p>
        <p style={{ marginBottom: '12px', marginTop: '16px' }}>
          Enter the administrator password to confirm:
        </p>
        <Input.Password
          placeholder="Enter password"
          value={permDeletePassword}
          onChange={(e) => setPermDeletePassword(e.target.value)}
          onPressEnter={() => {
            if (permDeletePassword === PERMANENT_DELETE_PASSWORD) {
              handleConfirmPermDelete();
            }
          }}
        />
      </Modal>
    </div>
  );
};

// Dogs List Component
const DogsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [breedFilter, setBreedFilter] = useState('all'); // New state for breed filter
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editingDog, setEditingDog] = useState(null);
  const [deletingDog, setDeletingDog] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [form] = Form.useForm();

  // Fetch ALL dogs at once
  const { loading, error, data, refetch } = useQuery(GET_ALL_DOGS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null },
  });

  const [updateDog] = useMutation(UPDATE_DOG_BY_ADMIN, {
    onCompleted: () => {
      message.success('Dog updated successfully!');
      setEditModalVisible(false);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to update dog: ${err.message}`);
    },
  });

  const [deleteDog, { loading: deleteLoading }] = useMutation(DELETE_DOG, {
    onCompleted: () => {
      message.success('Dog deleted successfully!');
      setDeleteModalVisible(false);
      setConfirmationText('');
      setDeletingDog(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to delete dog: ${err.message}`);
    },
  });

  // Sort by ID ascending
  const sortedData = data?.getAllRegisteredDogs?.data
    ? [...data.getAllRegisteredDogs.data].sort((a, b) => a.id - b.id)
    : [];

  // Client-side filtering based on search term AND breed filter
  const filteredData = sortedData.filter(dog => {
    // Search term filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      dog.call_name?.toLowerCase().includes(searchLower) ||
      dog.registration_name?.toLowerCase().includes(searchLower) ||
      dog.owner_name?.toLowerCase().includes(searchLower) ||
      dog.breed?.toLowerCase().includes(searchLower) ||
      dog.breed_mix?.toLowerCase().includes(searchLower)
    );

    // Breed filter
    let matchesBreedFilter = true;
    if (breedFilter === 'other') {
      matchesBreedFilter = dog.breed === 'Other';
    }

    return matchesSearch && matchesBreedFilter;
  });

  const handleEdit = (record) => {
    setEditingDog(record);
    form.setFieldsValue({
      call_name: record.call_name || '',
      registration_name: record.registration_name || '',
      breed: record.breed || '',
      breed_mix: record.breed_mix || '',
      gender: record.gender || '',
      status: record.status || '',
      date_of_birth: record.date_of_birth || '',
      rescue: record.rescue || false,
      dogImage: record.dogImage || '',
      title: record.title || '',
      titleDate: record.titleDate || '',
      qs: record.qs || false,
      dogLegs: record.dogLegs || 0,
      notes: record.notes || '',
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      // Prepare update data (notes field is now supported in UpdateDogByAdminInput)
      const updateData = { ...values };

      // Convert empty dogLegs to null (backend expects number or null, not empty string)
      if (updateData.dogLegs === '' || updateData.dogLegs === undefined) {
        updateData.dogLegs = null;
      } else {
        updateData.dogLegs = parseInt(updateData.dogLegs, 10);
      }

      // Convert empty title to null
      if (updateData.title === '') {
        updateData.title = null;
      }

      // Convert empty titleDate to null
      if (updateData.titleDate === '') {
        updateData.titleDate = null;
      }

      // Convert empty notes to null
      if (updateData.notes === '') {
        updateData.notes = null;
      }

      await updateDog({
        variables: {
          id: editingDog.id,
          data: updateData,
        },
      });
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const handleDelete = (record) => {
    setDeletingDog(record);
    setConfirmationText('');
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmationText !== deletingDog?.call_name) {
      message.error('Dog name does not match. Please type the exact name to confirm.');
      return;
    }

    try {
      await deleteDog({
        variables: { id: deletingDog.id },
      });
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const columns = [
    {
      title: 'Dog ID',
      dataIndex: 'dog_id',
      key: 'dog_id',
      width: 100,
    },
    { title: 'Call Name', dataIndex: 'call_name', key: 'call_name' },
    { title: 'Registration Name', dataIndex: 'registration_name', key: 'registration_name' },
    { title: 'Owner', dataIndex: 'owner_name', key: 'owner_name' },
    {
      title: 'Breed',
      dataIndex: 'breed',
      key: 'breed',
      render: (breed, record) => {
        // Display breed_mix if breed is "Other" or contains "Mixed Breed"
        if (breed === 'Other' || breed?.toLowerCase().includes('mixed breed')) {
          return record.breed_mix || breed;
        }
        return breed;
      }
    },
    { title: 'Gender', dataIndex: 'gender', key: 'gender' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Payment Status', dataIndex: 'payment_status', key: 'payment_status' },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            size="small"
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: '#fff',
              opacity: 1,
              visibility: 'visible'
            }}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record);
            }}
            size="small"
            style={{
              backgroundColor: '#ff4d4f',
              borderColor: '#ff4d4f',
              color: '#fff'
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title="Database Admin - Dogs" />
      <div className="flex flex-col gap-4 mt-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Dogs ({filteredData.length} total)
          </h2>
        </div>
        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-md">
            <Search
              placeholder="Search dogs by name, owner, breed..."
              allowClear
              enterButton={
                <Button
                  type="primary"
                  style={{
                    backgroundColor: '#5A1E66',
                    borderColor: '#5A1E66',
                    color: '#fff'
                  }}
                >
                  Search
                </Button>
              }
              size="large"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Breed
            </label>
            <Select
              value={breedFilter}
              onChange={(value) => setBreedFilter(value)}
              size="large"
              style={{ width: '100%' }}
            >
              <Select.Option value="all">All Dogs</Select.Option>
              <Select.Option value="other">Other Breeds Only</Select.Option>
            </Select>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={false}
          loading={loading}
          className="border border-purple-100 rounded-lg"
          onRow={(record) => ({
            onClick: () => navigate(`/admin/database/dogs/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      {/* Edit Dog Modal */}
      <Modal
        title={`Edit Dog: ${editingDog?.call_name || ''}`}
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => setEditModalVisible(false)}
        width={900}
        okText="Save"
        okButtonProps={{
          type: 'primary',
          style: {
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }
        }}
      >
        <Form form={form} layout="vertical">
          <Tabs defaultActiveKey="1">
            {/* Tab 1: Basic Information */}
            <Tabs.TabPane tab="Basic Info" key="1">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item
                  name="call_name"
                  label="Call Name"
                  rules={[{ required: true, message: 'Please enter call name' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item name="registration_name" label="Registration Name">
                  <Input />
                </Form.Item>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="breed" label="Breed">
                  <Input />
                </Form.Item>
                <Form.Item name="breed_mix" label="Breed Mix">
                  <Input />
                </Form.Item>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <Form.Item name="gender" label="Gender">
                  <Select placeholder="Select gender">
                    <Select.Option value="Male Intact">Male Intact</Select.Option>
                    <Select.Option value="Male Neutered">Male Neutered</Select.Option>
                    <Select.Option value="Female Intact">Female Intact</Select.Option>
                    <Select.Option value="Female Spayed">Female Spayed</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="status" label="Status">
                  <Select placeholder="Select status">
                    <Select.Option value="Active">Active</Select.Option>
                    <Select.Option value="Inactive">Inactive</Select.Option>
                    <Select.Option value="Pending">Pending</Select.Option>
                    <Select.Option value="Deceased">Deceased</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="date_of_birth" label="Date of Birth">
                  <Input placeholder="YYYY-MM-DD" />
                </Form.Item>
              </div>
              <Form.Item name="rescue" valuePropName="checked">
                <Checkbox>Rescue Dog</Checkbox>
              </Form.Item>
            </Tabs.TabPane>

            {/* Tab 2: Titles & Achievements */}
            <Tabs.TabPane tab="Titles & Achievements" key="2">
              <Form.Item name="title" label="Title">
                <Input placeholder="e.g., SGN, SGA, SGE" />
              </Form.Item>
              <Form.Item name="titleDate" label="Title Date">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="dogLegs" label="Dog Legs">
                  <Input type="number" placeholder="Number of legs earned" />
                </Form.Item>
                <Form.Item name="qs" valuePropName="checked">
                  <Checkbox>QS Status</Checkbox>
                </Form.Item>
              </div>
            </Tabs.TabPane>

            {/* Tab 3: Admin & Notes */}
            <Tabs.TabPane tab="Admin & Notes" key="3">
              <Form.Item name="dogImage" label="Dog Image URL">
                <Input placeholder="https://..." />
              </Form.Item>
              <Form.Item name="notes" label="Admin Notes">
                <Input.TextArea rows={4} placeholder="Internal notes about this dog..." />
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>
        </Form>
      </Modal>

      {/* Delete Dog Confirmation Modal */}
      <Modal
        title="Delete Dog"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setConfirmationText('');
          setDeletingDog(null);
        }}
        width={600}
        okText="Delete"
        okButtonProps={{
          danger: true,
          disabled: confirmationText !== deletingDog?.call_name,
          loading: deleteLoading,
          style: {
            backgroundColor: confirmationText === deletingDog?.call_name ? '#ff4d4f' : undefined,
            borderColor: confirmationText === deletingDog?.call_name ? '#ff4d4f' : undefined,
            color: confirmationText === deletingDog?.call_name ? '#fff' : undefined
          }
        }}
        cancelText="Cancel"
      >
        <Alert
          message="Warning: This will permanently delete the dog"
          description="This action cannot be undone. The dog and all associated data will be permanently removed from the database."
          type="warning"
          showIcon
          style={{ marginBottom: '20px' }}
        />
        <p style={{ marginBottom: '10px' }}>
          <strong>Dog to delete:</strong> {deletingDog?.call_name} (ID: {deletingDog?.dog_id})
        </p>
        <p style={{ marginBottom: '10px' }}>
          <strong>Owner:</strong> {deletingDog?.owner_name}
        </p>
        <p style={{ marginBottom: '10px' }}>
          To confirm, please type the dog's call name: <strong>{deletingDog?.call_name}</strong>
        </p>
        <Input
          placeholder="Type dog's call name to confirm"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          style={{ marginTop: '10px' }}
        />
      </Modal>
    </div>
  );
};

// Dog Detail View Component
const DogDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dogId = parseInt(location.pathname.split('/').pop(), 10);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [form] = Form.useForm();

  // Scroll to top when component mounts or dogId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [dogId]);

  const { loading, error, data, refetch } = useQuery(GET_ALL_DOGS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null },
  });

  const { loading: sitLoading, data: sitData, refetch: sitRefetch } = useQuery(GET_DOG_SIT_BY_ID, {
    variables: { dogId },
    skip: !dogId,
  });

  const [updateDog] = useMutation(UPDATE_DOG_BY_ADMIN, {
    onCompleted: () => {
      message.success('Dog updated successfully!');
      setEditModalVisible(false);
      setEditSection(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to update dog: ${err.message}`);
    },
  });

  const [updateDogSIT] = useMutation(UPDATE_DOG_SIT, {
    onCompleted: () => {
      message.success('SIT status updated successfully!');
      setEditModalVisible(false);
      setEditSection(null);
      refetch();
      sitRefetch();
    },
    onError: (err) => {
      message.error(`Failed to update SIT: ${err.message}`);
    },
  });

  const [deleteDog] = useMutation(DELETE_DOG, {
    onCompleted: () => {
      message.success('Dog deleted successfully!');
      navigate('/admin/database/dogs');
    },
    onError: (err) => {
      message.error(`Failed to delete dog: ${err.message}`);
    },
  });

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Dog',
      content: `Are you sure you want to delete ${dog.call_name}? This action cannot be undone and will permanently remove all data associated with this dog.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        await deleteDog({
          variables: { id: dogId },
        });
      },
    });
  };

  if (loading || sitLoading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  const dog = data?.getAllRegisteredDogs?.data?.find(d => d.id === dogId);
  const sitInfo = sitData?.getRegisteredDogSITById;

  if (!dog) return (
    <div className="p-6">
      <Alert message="Dog Not Found" description="The requested dog could not be found." type="warning" showIcon />
    </div>
  );

  const handleEdit = (section) => {
    setEditSection(section);

    // Populate form based on section
    if (section === 'basic') {
      form.setFieldsValue({
        dog_id: dog.dog_id,
        call_name: dog.call_name,
        registration_name: dog.registration_name,
        date_of_birth: dog.date_of_birth,
      });
    } else if (section === 'breed') {
      form.setFieldsValue({
        breed: dog.breed,
        breed_mix: dog.breed_mix,
        gender: dog.gender,
        rescue: dog.rescue,
      });
    } else if (section === 'status') {
      form.setFieldsValue({
        status: dog.status,
        payment_status: dog.payment_status,
      });
    } else if (section === 'titles') {
      form.setFieldsValue({
        title: dog.title,
        titleDate: dog.titleDate,
        qs: dog.qs,
        dogLegs: dog.dogLegs,
      });
    } else if (section === 'sit') {
      form.setFieldsValue({
        birch: sitInfo?.birch,
        anise: sitInfo?.anise,
        clove: sitInfo?.clove,
      });
    } else if (section === 'notes') {
      form.setFieldsValue({
        notes: dog.notes,
      });
    }

    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      // Use different mutation for SIT fields
      if (editSection === 'sit') {
        await updateDogSIT({
          variables: {
            id: dogId,
            data: values,
          },
        });
      } else {
        // Remove dog_id from values as it's not accepted by UpdateDogByAdminInput schema
        const { dog_id, ...validValues } = values;

        // Always include call_name as it's required by the backend
        const updateData = {
          call_name: dog.call_name,
          ...validValues,
        };

        await updateDog({
          variables: {
            id: dogId,
            data: updateData,
          },
        });
      }
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const InfoSection = ({ title, children, onEdit }) => (
    <div className="mb-6">
      <div className="flex items-center mb-3 border-b border-purple-100 pb-2" style={{ gap: '20px' }}>
        <h3 className="text-lg font-semibold text-purple-600">{title}</h3>
        {onEdit && (
          <Button
            icon={<EditOutlined />}
            onClick={onEdit}
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: 'white',
            }}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );

  const InfoItem = ({ label, value }) => (
    <div className="bg-gray-50 p-3 rounded">
      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
      <p className="mt-1 text-base text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      {/* Header with Title and Back Button */}
      <PageInfo title={`Dog Details - ${dog.call_name || 'Unknown'}`}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/database/dogs')}
            size="large"
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: '#fff'
            }}
          >
            Back to Dogs
          </Button>
          <Button
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            size="large"
            danger
            type="primary"
          >
            Delete Dog
          </Button>
        </Space>
      </PageInfo>

      {/* Dog Information Sections */}
      <div className="mt-6 space-y-6">
        {/* Dog Image */}
        {dog.dogImage && (
          <div className="flex justify-center mb-6">
            <img
              src={dog.dogImage}
              alt={dog.call_name}
              className="w-64 h-64 object-cover rounded-lg shadow-lg border-4 border-purple-200"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Basic Information */}
        <InfoSection title="Basic Information" onEdit={() => handleEdit('basic')}>
          <InfoItem label="Dog ID" value={dog.dog_id} />
          <InfoItem label="Call Name" value={dog.call_name} />
          <InfoItem label="Registration Name" value={dog.registration_name} />
          <InfoItem label="Owner" value={dog.owner_name} />
          <InfoItem label="Owner Auth ID" value={dog.authId} />
          <InfoItem label="Date of Birth" value={dog.date_of_birth} />
        </InfoSection>

        {/* Breed Information */}
        <InfoSection title="Breed Information" onEdit={() => handleEdit('breed')}>
          <InfoItem label="Breed" value={dog.breed} />
          <InfoItem label="Breed Mix" value={dog.breed_mix} />
          <InfoItem label="Gender" value={dog.gender} />
          <InfoItem label="Rescue Dog" value={dog.rescue ? 'Yes' : 'No'} />
        </InfoSection>

        {/* Status Information */}
        <InfoSection title="Status Information" onEdit={() => handleEdit('status')}>
          <InfoItem label="Status" value={dog.status} />
          <InfoItem label="Payment Status" value={dog.payment_status} />
        </InfoSection>

        {/* Title & Achievement Information */}
        <InfoSection title="Titles & Achievements" onEdit={() => handleEdit('titles')}>
          <InfoItem label="Current Title" value={dog.title} />
          <InfoItem label="Title Date" value={dog.titleDate ? moment(dog.titleDate).format('MM/DD/YYYY') : 'N/A'} />
          <InfoItem label="Qualified (QS)" value={dog.qs === true ? 'Yes' : dog.qs === false ? 'No' : 'N/A'} />
          <InfoItem label="Dog Legs" value={dog.dogLegs} />
        </InfoSection>

        {/* SIT (Scent Identification Test) Information */}
        {sitInfo && (
          <InfoSection title="SIT (Scent Identification Test)" onEdit={() => handleEdit('sit')}>
            <InfoItem label="Birch" value={sitInfo.birch} />
            <InfoItem label="Anise" value={sitInfo.anise} />
            <InfoItem label="Clove" value={sitInfo.clove} />
          </InfoSection>
        )}

        {/* Admin Notes */}
        {dog.notes && (
          <InfoSection title="Admin Notes" onEdit={() => handleEdit('notes')}>
            <div className="col-span-2">
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-gray-900 whitespace-pre-wrap">{dog.notes}</p>
              </div>
            </div>
          </InfoSection>
        )}
      </div>

      {/* Edit Modals */}
      <Modal
        title={`Edit ${editSection === 'basic' ? 'Basic Information' :
                editSection === 'breed' ? 'Breed Information' :
                editSection === 'status' ? 'Status Information' :
                editSection === 'titles' ? 'Titles & Achievements' :
                editSection === 'sit' ? 'SIT Information' :
                editSection === 'notes' ? 'Admin Notes' : ''}`}
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => {
          setEditModalVisible(false);
          setEditSection(null);
          form.resetFields();
        }}
        width={700}
        okText="Save"
        okButtonProps={{
          type: 'primary',
          style: {
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }
        }}
      >
        <Form form={form} layout="vertical">
          {/* Basic Information Modal */}
          {editSection === 'basic' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="dog_id" label="Dog ID">
                  <Input type="number" />
                </Form.Item>
                <Form.Item name="call_name" label="Call Name" rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
              </div>
              <Form.Item name="registration_name" label="Registration Name">
                <Input />
              </Form.Item>
              <Form.Item name="date_of_birth" label="Date of Birth">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </>
          )}

          {/* Breed Information Modal */}
          {editSection === 'breed' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="breed" label="Breed">
                  <Input />
                </Form.Item>
                <Form.Item name="breed_mix" label="Breed Mix">
                  <Input />
                </Form.Item>
              </div>
              <Form.Item name="gender" label="Gender">
                <Select placeholder="Select gender">
                  <Select.Option value="Male Intact">Male Intact</Select.Option>
                  <Select.Option value="Male Neutered">Male Neutered</Select.Option>
                  <Select.Option value="Female Intact">Female Intact</Select.Option>
                  <Select.Option value="Female Spayed">Female Spayed</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="rescue" valuePropName="checked">
                <Checkbox>Rescue Dog</Checkbox>
              </Form.Item>
            </>
          )}

          {/* Status Information Modal */}
          {editSection === 'status' && (
            <>
              <Form.Item name="status" label="Status">
                <Select placeholder="Select status">
                  <Select.Option value="Active">Active</Select.Option>
                  <Select.Option value="Inactive">Inactive</Select.Option>
                  <Select.Option value="Pending">Pending</Select.Option>
                  <Select.Option value="Deceased">Deceased</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="payment_status" label="Payment Status">
                <Select placeholder="Select payment status">
                  <Select.Option value="Paid">Paid</Select.Option>
                  <Select.Option value="Pending">Pending</Select.Option>
                  <Select.Option value="Unpaid">Unpaid</Select.Option>
                </Select>
              </Form.Item>
            </>
          )}

          {/* Titles & Achievements Modal */}
          {editSection === 'titles' && (
            <>
              <Form.Item name="title" label="Title">
                <Input placeholder="e.g., SGN, SGA, SGE" />
              </Form.Item>
              <Form.Item name="titleDate" label="Title Date">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="dogLegs" label="Dog Legs">
                  <Input type="number" placeholder="Number of legs earned" />
                </Form.Item>
                <Form.Item name="qs" valuePropName="checked">
                  <Checkbox>QS Status</Checkbox>
                </Form.Item>
              </div>
            </>
          )}

          {/* SIT Information Modal */}
          {editSection === 'sit' && (
            <>
              <Form.Item name="birch" label="Birch">
                <Select placeholder="Select SIT status">
                  <Select.Option value="Pass">Pass</Select.Option>
                  <Select.Option value="Miss">Miss</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="anise" label="Anise">
                <Select placeholder="Select SIT status">
                  <Select.Option value="Pass">Pass</Select.Option>
                  <Select.Option value="Miss">Miss</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="clove" label="Clove">
                <Select placeholder="Select SIT status">
                  <Select.Option value="Pass">Pass</Select.Option>
                  <Select.Option value="Miss">Miss</Select.Option>
                </Select>
              </Form.Item>
            </>
          )}

          {/* Admin Notes Modal */}
          {editSection === 'notes' && (
            <Form.Item name="notes" label="Admin Notes">
              <Input.TextArea rows={6} placeholder="Internal notes about this dog..." />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

// Trial Results List Component
const TrialResultsList = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch ALL trial results at once
  const { loading, error, data } = useQuery(GET_ALL_TRIAL_RESULTS, {
    variables: { pageNumber: 1, pageSize: 10000 },
  });

  // Sort by ID ascending
  const sortedData = data?.getAllTrialResult?.data
    ? [...data.getAllTrialResult.data].sort((a, b) => a.id - b.id)
    : [];

  // Client-side filtering based on search term
  const filteredData = sortedData.filter(result => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      result.handler?.toLowerCase().includes(searchLower) ||
      result.dog?.toLowerCase().includes(searchLower) ||
      result.breed?.toLowerCase().includes(searchLower) ||
      result.div?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Handler', dataIndex: 'handler', key: 'handler' },
    { title: 'Dog', dataIndex: 'dog', key: 'dog' },
    { title: 'Breed', dataIndex: 'breed', key: 'breed' },
    { title: 'Division', dataIndex: 'div', key: 'div' },
    { title: 'Q/NQ', dataIndex: 'qNq', key: 'qNq' },
    { title: 'Points', dataIndex: 'points', key: 'points' },
    { title: 'Place', dataIndex: 'place', key: 'place' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title="Database Admin - Trial Results" />
      <div className="flex flex-col gap-4 mt-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Trial Results ({filteredData.length} total)
          </h2>
        </div>
        <Search
          placeholder="Search trial results by handler, dog, breed, division..."
          allowClear
          enterButton={
            <Button
              type="primary"
              style={{
                backgroundColor: '#5A1E66',
                borderColor: '#5A1E66',
                color: '#fff'
              }}
            >
              Search
            </Button>
          }
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>
      <div className="mt-6">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={false}
          loading={loading}
          className="border border-purple-100 rounded-lg"
        />
      </div>
    </div>
  );
};

// Hosts List Component
const HostsList = () => {
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch ALL hosts at once
  const { loading, error, data } = useQuery(GET_ALL_HOSTS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null },
  });

  // Sort by name
  const sortedData = data?.getAllHost?.data
    ? [...data.getAllHost.data].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : [];

  // Client-side filtering based on search term
  const filteredData = sortedData.filter(host => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      host.name?.toLowerCase().includes(searchLower) ||
      host.email?.toLowerCase().includes(searchLower) ||
      host.phone_number?.toLowerCase().includes(searchLower) ||
      host.city?.toLowerCase().includes(searchLower) ||
      host.state?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone_number', key: 'phone_number' },
    { title: 'City', dataIndex: 'city', key: 'city' },
    { title: 'State', dataIndex: 'state', key: 'state' },
    { title: 'Status', dataIndex: 'hostStatus', key: 'hostStatus' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title="Database Admin - Hosts" />
      <div className="flex flex-col gap-4 mt-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Hosts ({filteredData.length} total)
          </h2>
        </div>
        <Search
          placeholder="Search hosts by name, email, city, state..."
          allowClear
          enterButton={
            <Button
              type="primary"
              style={{
                backgroundColor: '#5A1E66',
                borderColor: '#5A1E66',
                color: '#fff'
              }}
            >
              Search
            </Button>
          }
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>
      <div className="mt-6">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="authId"
          pagination={false}
          loading={loading}
          className="border border-purple-100 rounded-lg"
        />
      </div>
    </div>
  );
};

// Users List Component (using Members query since getAllUsers has type mismatch)
const UsersList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [form] = Form.useForm();

  // Fetch ALL users at once
  const { loading, error, data, refetch } = useQuery(GET_ALL_MEMBERS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null },
  });

  const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_USER_BY_ADMIN, {
    onCompleted: (data) => {
      console.log('updateUser completed:', data);
      message.success('User profile updated successfully!');
    },
    onError: (err) => {
      console.error('updateUser error:', err);
      message.error(`Failed to update user profile: ${err.message}`);
    },
  });

  const [updateUserRoles, { loading: updateRolesLoading }] = useMutation(UPDATE_USER_ROLES, {
    onCompleted: (data) => {
      console.log('updateUserRoles completed:', data);
      message.success('User roles updated successfully! Auth0 metadata has been updated.');
    },
    onError: (err) => {
      console.error('updateUserRoles error:', err);
      message.error(`Failed to update user roles: ${err.message}`);
    },
  });

  const [deactivateUser, { loading: deleteLoading }] = useMutation(DEACTIVATE_USER, {
    onCompleted: () => {
      message.success('User deactivated successfully! All associated data has been blocked.');
      setDeleteModalVisible(false);
      setConfirmationText('');
      setDeletingUser(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to deactivate user: ${err.message}`);
    },
  });

  const [reactivateUser, { loading: reactivateLoading }] = useMutation(REACTIVATE_USER, {
    onCompleted: () => {
      message.success('User reactivated successfully! User can now access the system.');
      setDeleteModalVisible(false);
      setConfirmationText('');
      setDeletingUser(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to reactivate user: ${err.message}`);
    },
  });

  // Sort by ID ascending
  const sortedData = data?.getAllRegisteredMember?.data
    ? [...data.getAllRegisteredMember.data].sort((a, b) => a.id - b.id)
    : [];

  // Client-side filtering based on search term
  const filteredData = sortedData.filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.phone_number?.toLowerCase().includes(searchLower)
    );
  });

  const handleEdit = (record) => {
    setEditingUser(record);
    const nameParts = record.name?.split(' ') || ['', ''];
    form.setFieldsValue({
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
      phone_number: record.phone_number || '',
      city: record.city || '',
      state: record.state || '',
      role: record.role || [],
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      console.log('Form values:', values);
      console.log('Editing user:', editingUser);

      // Get original roles to check if they changed (create copies to avoid mutating read-only arrays)
      const originalRoles = [...(editingUser.role || [])];
      const newRoles = [...(values.role || [])];
      const rolesChanged = JSON.stringify(originalRoles.sort()) !== JSON.stringify(newRoles.sort());

      console.log('Roles changed:', rolesChanged, 'Original:', originalRoles, 'New:', newRoles);

      // If roles changed, call updateUserRoles (updates Auth0 metadata)
      if (rolesChanged) {
        console.log('Updating roles via updateUserRoles...');
        await updateUserRoles({
          variables: {
            authId: editingUser.authId,
            role: values.role,  // Use original values.role, not sorted copy
          },
        });
      }

      // Always update all fields using updateUserByAdmin (including role for database consistency)
      console.log('Updating user with all data:', values);

      await updateUser({
        variables: {
          authId: editingUser.authId,
          data: values,  // Send ALL values including role
        },
      });

      console.log('Update successful, closing modal and refetching...');

      // Close modal and refetch data
      setEditModalVisible(false);
      refetch();
    } catch (err) {
      console.error('Update failed:', err);
      message.error(`Update failed: ${err.message}`);
    }
  };

  const handleDelete = (record) => {
    setDeletingUser(record);
    setConfirmationText('');
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmationText !== deletingUser?.name) {
      message.error('User name does not match. Please type the exact name to confirm.');
      return;
    }

    try {
      // Check if user is currently blocked - if so, reactivate; otherwise deactivate
      if (deletingUser.isBlocked) {
        await reactivateUser({
          variables: {
            authId: deletingUser.authId,
          },
        });
      } else {
        await deactivateUser({
          variables: {
            authId: deletingUser.authId,
          },
        });
      }
    } catch (err) {
      console.error('Action failed:', err);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone_number', key: 'phone_number' },
    { title: 'City', dataIndex: 'city', key: 'city' },
    { title: 'State', dataIndex: 'state', key: 'state' },
    { title: 'Roles', dataIndex: 'role', key: 'role', render: (roles) => roles?.join(', ') || 'N/A' },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" style={{ display: 'flex' }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
            size="small"
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: '#fff',
              opacity: 1,
              visibility: 'visible'
            }}
          >
            Edit
          </Button>
          <Button
            danger={!record.isBlocked}
            icon={<DeleteOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record);
            }}
            size="small"
            style={{
              backgroundColor: record.isBlocked ? '#52c41a' : undefined,
              borderColor: record.isBlocked ? '#52c41a' : undefined,
              color: record.isBlocked ? '#fff' : undefined,
              opacity: 1,
              visibility: 'visible'
            }}
          >
            {record.isBlocked ? 'Reactivate' : 'Deactivate'}
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title="Database Admin - Users" />
      <div className="flex flex-col gap-4 mt-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Users ({filteredData.length} total)
          </h2>
        </div>
        <Search
          placeholder="Search users by name, email, phone..."
          allowClear
          enterButton={
            <Button
              type="primary"
              style={{
                backgroundColor: '#5A1E66',
                borderColor: '#5A1E66',
                color: '#fff'
              }}
            >
              Search
            </Button>
          }
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-md"
        />
      </div>
      <div className="mt-6">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={false}
          loading={loading}
          className="border border-purple-100 rounded-lg"
          onRow={(record) => ({
            onClick: () => navigate(`/admin/database/users/${encodeURIComponent(record.authId)}`),
            style: { cursor: 'pointer' }
          })}
        />
      </div>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => setEditModalVisible(false)}
        width={600}
        okText="Save"
        confirmLoading={updateUserLoading || updateRolesLoading}
        okButtonProps={{
          type: 'primary',
          style: {
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="phone_number" label="Phone Number">
            <Input />
          </Form.Item>
          <Form.Item name="city" label="City">
            <Input />
          </Form.Item>
          <Form.Item name="state" label="State">
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Roles">
            <Select
              mode="multiple"
              placeholder="Select roles"
              onSelect={() => {
                // Close dropdown after selecting an item
                setTimeout(() => {
                  document.activeElement?.blur();
                }, 100);
              }}
            >
              <Select.Option value="Member">Member</Select.Option>
              <Select.Option value="Judge">Judge</Select.Option>
              <Select.Option value="Host">Host</Select.Option>
              <Select.Option value="Super Admin">Super Admin</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Deactivate/Reactivate User Confirmation Modal */}
      <Modal
        title={deletingUser?.isBlocked ? "Reactivate User" : "Deactivate User"}
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setConfirmationText('');
          setDeletingUser(null);
        }}
        width={600}
        okText={deletingUser?.isBlocked ? "Reactivate" : "Deactivate"}
        okButtonProps={{
          danger: !deletingUser?.isBlocked,
          disabled: confirmationText !== deletingUser?.name,
          loading: deletingUser?.isBlocked ? reactivateLoading : deleteLoading,
          style: {
            backgroundColor: confirmationText === deletingUser?.name
              ? (deletingUser?.isBlocked ? '#52c41a' : '#ff4d4f')
              : undefined,
            borderColor: confirmationText === deletingUser?.name
              ? (deletingUser?.isBlocked ? '#52c41a' : '#ff4d4f')
              : undefined,
            color: confirmationText === deletingUser?.name ? '#fff' : undefined
          }
        }}
        cancelText="Cancel"
      >
        <Alert
          message={deletingUser?.isBlocked
            ? "This will reactivate the user and unblock their Auth0 account"
            : "Warning: This will deactivate the user and block their Auth0 account"}
          description={deletingUser?.isBlocked
            ? "The user will be able to access the system again. This action affects their member profile and Auth0 account."
            : "The user will be blocked and unable to access the system. This action affects their member profile and Auth0 account."}
          type={deletingUser?.isBlocked ? "info" : "warning"}
          showIcon
          style={{ marginBottom: '20px' }}
        />
        <p style={{ marginBottom: '10px' }}>
          <strong>User to {deletingUser?.isBlocked ? 'reactivate' : 'deactivate'}:</strong> {deletingUser?.name} ({deletingUser?.email})
        </p>
        <p style={{ marginBottom: '10px' }}>
          To confirm, please type the user's full name: <strong>{deletingUser?.name}</strong>
        </p>
        <Input
          placeholder="Type user name to confirm"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          style={{ marginTop: '10px' }}
        />
      </Modal>
    </div>
  );
};

// Payments List Component
const PaymentsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');
  const [statusLoadingMap, setStatusLoadingMap] = useState({});

  const handleFilterChange = (value) => {
    setPaymentStatusFilter(value);
  };

  // Fetch ALL payments at once
  const { loading, error, data, refetch } = useQuery(GET_ALL_PAYMENTS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null },
  });

  // Sort by ID ascending and filter out records without dogs
  const sortedData = data?.getAllRegisteredDogsByAdmin?.data
    ? [...data.getAllRegisteredDogsByAdmin.data]
        .filter(record => {
          // Only include records that have a dog registered (either call_name or registration_name exists)
          const hasDog = record.call_name || record.registration_name;
          return hasDog;
        })
        .sort((a, b) => a.id - b.id)
    : [];

  // Client-side filtering based on search term and payment status
  const filteredData = sortedData.filter(payment => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        payment.call_name?.toLowerCase().includes(searchLower) ||
        payment.owner_name?.toLowerCase().includes(searchLower) ||
        payment.email?.toLowerCase().includes(searchLower) ||
        payment.phone_number?.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    // Payment status filter
    if (paymentStatusFilter !== 'All') {
      // Normalize the payment status (handle null/undefined as "Unpaid")
      const normalizedStatus = payment.payment_status || 'Unpaid';
      if (normalizedStatus !== paymentStatusFilter) return false;
    }

    return true;
  });

  // Handle payment status change
  const onChangeStatus = async (authId, status) => {
    setStatusLoadingMap(prev => ({ ...prev, [authId]: true }));
    try {
      const result = await client.mutate({
        mutation: UPDATE_PAYMENT_STATUS,
        variables: { authId: authId, data: { payment_status: status } },
      });

      if (result?.data?.updateRegisteredDogMarkPaid) {
        message.success(result?.data?.updateRegisteredDogMarkPaid?.message || 'Payment status updated successfully');
        refetch(); // Refresh the data
      }
    } catch (error) {
      message.error(error?.message || 'Failed to update payment status');
    } finally {
      setStatusLoadingMap(prev => ({ ...prev, [authId]: false }));
    }
  };

  // Render payment status badge or dropdown
  const renderPaymentStatus = (status, authId) => {
    const isLoading = statusLoadingMap[authId];

    if (isLoading) {
      return <Spin size="small" />;
    }

    // Normalize status - treat null/undefined/empty as "Unpaid"
    const normalizedStatus = status || 'Unpaid';

    if (normalizedStatus === 'Unpaid') {
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            width="w-[150px]"
            placeholder="Unpaid"
            dropdownOptions={['Mark as Paid']}
            customContent="justify-center"
            mainContent="w-100%"
            onChange={(option) => onChangeStatus(authId, option)}
          />
        </div>
      );
    }

    if (normalizedStatus === 'Paid') {
      return (
        <div className="flex justify-center border rounded-md py-2 border-[#17B26A] bg-[#F6FEF9] w-[150px]">
          <span className="text-base font-regular text-[#17B26A]">Paid</span>
          <img src={successPayment} className="ml-1" alt="" />
        </div>
      );
    } else if (normalizedStatus === 'Mark as Paid') {
      return (
        <div className="flex justify-center border rounded-md py-2 border-gray-300 bg-white w-[150px]">
          <span className="text-base font-regular text-gray-600">Mark as Paid</span>
          <img src={pendingPayment} className="ml-1" alt="" />
        </div>
      );
    }
    return <span className="text-gray-500">N/A</span>;
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: 'Owner Name',
      dataIndex: 'owner_name',
      key: 'owner_name',
      render: (text) => <span className="font-medium text-gray-900">{text || 'N/A'}</span>
    },
    {
      title: 'Dog Name',
      dataIndex: 'call_name',
      key: 'call_name',
      render: (text, record) => <span className="text-gray-700">{text || record.registration_name || 'N/A'}</span>
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <span className="text-gray-600">{text || 'N/A'}</span>
    },
    {
      title: 'Phone Number',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (text) => <span className="text-gray-600">{text || 'N/A'}</span>
    },
    {
      title: 'Member Since',
      dataIndex: 'dateOfJoining',
      key: 'dateOfJoining',
      render: (date) => date ? moment(date).format('MM/DD/YYYY') : 'N/A'
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <span className="font-semibold text-gray-900">${amount || 0}</span>
    },
    {
      title: 'Payment Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status, record) => renderPaymentStatus(status, record.authId)
    },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title="Database Admin - Payments" />

      {/* Header with title and count */}
      <div className="mt-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Detail of Members ({filteredData.length} {filteredData.length === 1 ? 'result' : 'results'})
        </h2>
      </div>

      {/* Filters Section */}
      <div className="flex flex-col md:flex-row gap-4 mt-4">
        {/* Search Bar */}
        <Search
          placeholder="Search by owner, dog name, email, phone..."
          allowClear
          enterButton={
            <Button
              type="primary"
              style={{
                backgroundColor: '#5A1E66',
                borderColor: '#5A1E66',
                color: '#fff'
              }}
            >
              Search
            </Button>
          }
          size="large"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 max-w-md"
        />

        {/* Payment Status Filter */}
        <Select
          value={paymentStatusFilter}
          onChange={handleFilterChange}
          size="large"
          style={{ width: 200 }}
          options={[
            { value: 'All', label: 'All Payment Status' },
            { value: 'Paid', label: 'Paid' },
            { value: 'Mark as Paid', label: 'Mark as Paid' },
            { value: 'Unpaid', label: 'Unpaid' },
          ]}
        />
      </div>

      {/* Table */}
      <div className="mt-6">
        <Table
          key={`payments-table-${paymentStatusFilter}-${searchTerm}`}
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) => record.id || `no-id-${record.email}-${record.owner_name}`}
          pagination={false}
          loading={loading}
          className="border border-purple-100 rounded-lg"
          onRow={(record) => ({
            onClick: () => navigate(`/admin/database/payments/${record.id}`),
            style: { cursor: 'pointer' },
            className: 'hover:bg-purple-50 transition-colors'
          })}
        />
      </div>
    </div>
  );
};

// Payment Detail View Component
const PaymentDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const paymentId = parseInt(location.pathname.split('/').pop());

  const { loading, error, data } = useQuery(GET_ALL_PAYMENTS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null },
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  const payment = data?.getAllRegisteredDogsByAdmin?.data?.find(p => p.id === paymentId);

  if (!payment) return (
    <div className="p-6">
      <Alert message="Payment Not Found" description="The requested payment could not be found." type="warning" showIcon />
    </div>
  );

  const InfoSection = ({ title, children }) => (
    <div className="mb-6">
      <div className="flex items-center mb-3 border-b border-purple-100 pb-2" style={{ gap: '20px' }}>
        <h3 className="text-lg font-semibold text-purple-600">{title}</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );

  const InfoItem = ({ label, value, fullWidth = false }) => (
    <div className={fullWidth ? 'md:col-span-2' : ''}>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-base font-medium text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title={`Payment Details - ${payment.call_name || 'Unknown'}`}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/payments')}
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Payments
        </Button>
      </PageInfo>

      <div className="mt-8 max-w-4xl">
        {/* Dog Information */}
        <InfoSection title="Dog Information">
          <InfoItem label="Payment ID" value={payment.id} />
          <InfoItem label="Dog Call Name" value={payment.call_name} />
          <InfoItem label="Registration Name" value={payment.registration_name} fullWidth />
        </InfoSection>

        {/* Owner Information */}
        <InfoSection title="Owner Information">
          <InfoItem label="Owner Name" value={payment.owner_name} />
          <InfoItem label="Email" value={payment.email} />
          <InfoItem label="Phone Number" value={payment.phone_number} />
          <InfoItem label="Auth ID" value={payment.authId} fullWidth />
        </InfoSection>

        {/* Payment Information */}
        <InfoSection title="Payment Information">
          <InfoItem label="Amount" value={payment.amount ? `$${payment.amount}` : 'N/A'} />
          <InfoItem label="Payment Status" value={payment.payment_status} />
          <InfoItem label="Dog Status" value={payment.status} />
          <InfoItem
            label="Date of Joining"
            value={payment.dateOfJoining ? moment(payment.dateOfJoining).format('MM/DD/YYYY') : 'N/A'}
          />
        </InfoSection>
      </div>

      {/* Back Button at Bottom */}
      <div className="mt-8 pt-6 border-t">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/payments')}
          size="large"
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Payments
        </Button>
      </div>
    </div>
  );
};

// Trial Detail View Component
const TrialDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const trialId = parseInt(location.pathname.split('/').pop());
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [form] = Form.useForm();

  // Edit Fees Modal State
  const [isEditFeesModalVisible, setIsEditFeesModalVisible] = useState(false);
  const [editingSearches, setEditingSearches] = useState([]);

  // Entries Modal State
  const [isEntriesModalVisible, setIsEntriesModalVisible] = useState(false);
  const [selectedSearch, setSelectedSearch] = useState(null);

  // Collapsible sections state
  const [isTrialFeesExpanded, setIsTrialFeesExpanded] = useState(false);
  const [isTrialEntriesExpanded, setIsTrialEntriesExpanded] = useState(false);

  // Scroll to top when component mounts or trialId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [trialId]);

  const { loading, error, data, refetch } = useQuery(GET_TRIAL_BY_ID, {
    variables: { trialCalendarId: trialId },
  });

  // Fetch dog entries for this trial
  const { loading: entriesLoading, data: entriesData } = useQuery(GET_DOG_ENTRIES_BY_TRIAL, {
    variables: {
      trialCalendarId: trialId,
      pageNumber: 1,
      pageSize: 1000, // Get all entries
      search: '',
    },
    skip: !trialId || !data?.getTrialCalendarById, // Skip if no trial ID or trial data not loaded yet
  });

  const [updateTrial] = useMutation(UPDATE_TRIAL_BY_ADMIN, {
    onCompleted: () => {
      message.success('Trial updated successfully!');
      setEditModalVisible(false);
      setEditSection(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to update trial: ${err.message}`);
    },
  });

  const [updateApprovalStatus] = useMutation(UPDATE_TRIAL_APPROVAL_STATUS, {
    onCompleted: () => {
      message.success('Approval status updated successfully!');
      setEditModalVisible(false);
      setEditSection(null);
      refetch();
    },
    onError: (err) => {
      message.error(`Failed to update approval status: ${err.message}`);
    },
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  const trial = data?.getTrialCalendarById;
  if (!trial) return (
    <div className="p-6">
      <Alert message="Trial Not Found" description="The requested trial could not be found." type="warning" showIcon />
    </div>
  );

  const handleEditSection = (section) => {
    setEditSection(section);

    // Pre-fill form based on section
    if (section === 'basic') {
      form.setFieldsValue({
        trialName: trial.trail?.trialName || '',
        status: trial.trail?.status || 'Upcoming',
        noOfJudge: trial.trail?.noOfJudge || null,
        maxEntries: trial.trail?.maxEntries || null,
      });
    } else if (section === 'approval') {
      form.setFieldsValue({
        approvalStatus: trial.trail?.approvalStatus || 'Pending',
      });
    } else if (section === 'location') {
      form.setFieldsValue({
        trialLocationName: trial.trail?.trialLocationName || '',
        trialLocationAddress: trial.trail?.trialLocationAddress || '',
        city: trial.trail?.city || '',
        state: trial.trail?.state || '',
        zip: trial.trail?.zip || '',
        timezone: trial.trail?.timezone || '',
      });
    } else if (section === 'dates') {
      form.setFieldsValue({
        trialDate: trial.trail?.trialDate ? moment.utc(trial.trail.trialDate) : null,
        openDate: trial.trail?.openDate ? moment.utc(trial.trail.openDate) : null,
        closeDate: trial.trail?.closeDate ? moment.utc(trial.trail.closeDate) : null,
        cutoffDate: trial.trail?.cutoffDate ? moment.utc(trial.trail.cutoffDate) : null,
      });
    } else if (section === 'additional') {
      form.setFieldsValue({
        description: trial.trail?.description || '',
        notes: trial.trail?.notes || '',
      });
    }

    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      // Handle approval status separately
      if (editSection === 'approval') {
        await updateApprovalStatus({
          variables: {
            id: parseInt(trial.trail?.id, 10),
            approvalStatus: values.approvalStatus,
          },
        });
        return;
      }

      // Prepare update data with full structure (WITHOUT approvalStatus)
      const updateData = {
        host: {
          id: parseInt(trial.host?.id, 10),
          hostName: trial.host?.hostName || '',
          contactAddress: trial.host?.contactAddress || '',
          city: trial.host?.city || '',
          trialSecretaryName: trial.host?.trialSecretaryName || '',
          state: trial.host?.state || '',
          trialSecretaryEmail: trial.host?.trialSecretaryEmail || '',
          zip: trial.host?.zip || '',
          phoneNumber: trial.host?.phoneNumber || '',
          paypalEmail: trial.host?.paypalEmail || '',
          paypalPaymentLink: trial.host?.paypalPaymentLink || '',
        },
        trial: {
          id: parseInt(trial.trail?.id, 10),
          trialName: values.trialName || trial.trail?.trialName || '',
          trialLocationName: values.trialLocationName || trial.trail?.trialLocationName || '',
          trialLocationAddress: values.trialLocationAddress || trial.trail?.trialLocationAddress || '',
          city: values.city || trial.trail?.city || '',
          state: values.state || trial.trail?.state || '',
          zip: values.zip || trial.trail?.zip || '',
          timezone: values.timezone || trial.trail?.timezone || '',
          trialDate: values.trialDate ? values.trialDate.toISOString() : (trial.trail?.trialDate ? new Date(trial.trail.trialDate).toISOString() : null),
          openDate: values.openDate ? values.openDate.toISOString() : (trial.trail?.openDate ? new Date(trial.trail.openDate).toISOString() : null),
          closeDate: values.closeDate ? values.closeDate.toISOString() : (trial.trail?.closeDate ? new Date(trial.trail.closeDate).toISOString() : null),
          cutoffDate: values.cutoffDate ? values.cutoffDate.toISOString() : (trial.trail?.cutoffDate ? new Date(trial.trail.cutoffDate).toISOString() : null),
          trialFile: trial.trail?.trialFile || '',
          description: values.description || trial.trail?.description || '',
          notes: values.notes || trial.trail?.notes || '',
          status: values.status || trial.trail?.status || 'Upcoming',
          noOfJudge: values.noOfJudge || trial.trail?.noOfJudge || null,
          trialFees: null,
          maxEntries: values.maxEntries || trial.trail?.maxEntries || null,
          trialImages: trial.trail?.trialImages || '',
          judgeAuthId: trial.trail?.judgeAuthId || [],
        },
        searches: (trial.searches || []).map(search => ({
          level: search.level,
          element: search.element || null,
          searchName: search.searchName,
          entryFee: parseInt(search.entryFee, 10),
        })),
      };

      await updateTrial({
        variables: {
          trialCalendarId: parseInt(trial.id, 10),
          data: updateData,
        },
      });
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  // Edit Fees Handlers
  const handleEditFees = () => {
    // Initialize the searches array for editing
    const searches = Array.isArray(trial.searches) ? trial.searches : [];
    setEditingSearches(searches.map((search, index) => ({
      key: index,
      id: search.id,
      level: search.level || '',
      element: search.element || '',
      searchName: search.searchName || '',
      entryFee: search.entryFee || 0,
    })));
    setIsEditFeesModalVisible(true);
  };

  const handleAddSearch = () => {
    const newSearch = {
      key: editingSearches.length,
      id: null, // New search, no ID yet
      level: '',
      element: '',
      searchName: '',
      entryFee: 0,
    };
    setEditingSearches([...editingSearches, newSearch]);
  };

  const handleRemoveSearch = (key) => {
    setEditingSearches(editingSearches.filter(search => search.key !== key));
  };

  const handleSearchChange = (key, field, value) => {
    setEditingSearches(editingSearches.map(search =>
      search.key === key ? { ...search, [field]: value } : search
    ));
  };

  const handleSaveFees = async () => {
    try {
      // Validate that all searches have required fields
      const invalidSearches = editingSearches.filter(s =>
        !s.level || !s.searchName || s.entryFee <= 0
      );

      if (invalidSearches.length > 0) {
        message.error('All searches must have a level, search name, and entry fee greater than 0');
        return;
      }

      // Prepare the update data with the full structure required by the backend
      const searches = editingSearches.map(search => ({
        level: search.level,
        element: search.element || null,
        searchName: search.searchName,
        entryFee: parseInt(search.entryFee, 10),
      }));

      // Use the EXACT same structure as handleUpdate to ensure compatibility
      const updateData = {
        host: {
          id: parseInt(trial.host?.id, 10),
          hostName: trial.host?.hostName || '',
          contactAddress: trial.host?.contactAddress || '',
          city: trial.host?.city || '',
          trialSecretaryName: trial.host?.trialSecretaryName || '',
          state: trial.host?.state || '',
          trialSecretaryEmail: trial.host?.trialSecretaryEmail || '',
          zip: trial.host?.zip || '',
          phoneNumber: trial.host?.phoneNumber || '',
          paypalEmail: trial.host?.paypalEmail || '',
          paypalPaymentLink: trial.host?.paypalPaymentLink || '',
        },
        trial: {
          id: parseInt(trial.trail?.id, 10),
          trialName: trial.trail?.trialName || '',
          trialLocationName: trial.trail?.trialLocationName || '',
          trialLocationAddress: trial.trail?.trialLocationAddress || '',
          city: trial.trail?.city || '',
          state: trial.trail?.state || '',
          zip: trial.trail?.zip || '',
          timezone: trial.trail?.timezone || '',
          trialDate: trial.trail?.trialDate ? new Date(trial.trail.trialDate).toISOString() : null,
          openDate: trial.trail?.openDate ? new Date(trial.trail.openDate).toISOString() : null,
          closeDate: trial.trail?.closeDate ? new Date(trial.trail.closeDate).toISOString() : null,
          cutoffDate: trial.trail?.cutoffDate ? new Date(trial.trail.cutoffDate).toISOString() : null,
          trialFile: trial.trail?.trialFile || '',
          description: trial.trail?.description || '',
          notes: trial.trail?.notes || '',
          status: trial.trail?.status || 'Upcoming',
          noOfJudge: trial.trail?.noOfJudge || null,
          trialFees: null, // Trial fees are unused - entry fees are in searches array
          maxEntries: trial.trail?.maxEntries || null,
          trialImages: trial.trail?.trialImages || '',
          judgeAuthId: trial.trail?.judgeAuthId || [],
        },
        searches: searches, // Updated searches array with new entry fees
      };

      await updateTrial({
        variables: {
          trialCalendarId: parseInt(trial.id, 10),
          data: updateData,
        },
      });

      message.success('Entry fees updated successfully!');
      refetch(); // Refresh the trial data
      setIsEditFeesModalVisible(false);
      setEditingSearches([]);
    } catch (err) {
      console.error('Failed to save fees - Full error:', err);
      message.error(`Failed to update trial: ${err.message}`);
    }
  };

  const InfoSection = ({ title, children, onEdit }) => (
    <div className="mb-6">
      <div className="flex items-center mb-3 border-b border-purple-100 pb-2" style={{ gap: '20px' }}>
        <h3 className="text-lg font-semibold text-purple-600">{title}</h3>
        {onEdit && (
          <Button
            icon={<EditOutlined />}
            onClick={onEdit}
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: 'white',
            }}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );

  const InfoItem = ({ label, value, fullWidth = false }) => (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-base text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      {/* Header with Title and Back Button */}
      <PageInfo title={`Trial Details - ${trial.trail?.trialName || 'Unknown'}`}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/trials')}
          size="large"
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Trials
        </Button>
      </PageInfo>

      {/* Trial Information Sections */}
      <div className="space-y-6">

        {/* Basic Information */}
        <InfoSection title="Basic Information" onEdit={() => handleEditSection('basic')}>
          <InfoItem label="Trial ID" value={trial.id} />
          <InfoItem label="Trial Name" value={trial.trail?.trialName} fullWidth />
          <InfoItem label="Trial Date" value={trial.trail?.trialDate ? moment.utc(trial.trail.trialDate).format('MM/DD/YYYY') : 'N/A'} />
          <InfoItem label="Status" value={trial.trail?.status} />
          <InfoItem label="Number of Judges" value={trial.trail?.noOfJudge} />
          <InfoItem label="Max Entries" value={trial.trail?.maxEntries} />
        </InfoSection>

        {/* Trial Fees / Entry Fees */}
        <div className="mb-6">
          <div className="flex items-center mb-3 border-b border-purple-100 pb-2" style={{ gap: '20px' }}>
            <h3 className="text-lg font-semibold text-purple-600">Trial Fees</h3>
            <Button
              icon={<EditOutlined />}
              onClick={handleEditFees}
              style={{
                backgroundColor: '#5A1E66',
                borderColor: '#5A1E66',
                color: 'white',
              }}
            >
              Edit Fees
            </Button>
            {trial.searches && trial.searches.length > 3 && (
              <Button
                type="text"
                icon={isTrialFeesExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setIsTrialFeesExpanded(!isTrialFeesExpanded)}
                style={{ color: '#5A1E66', marginLeft: 'auto' }}
              >
                {isTrialFeesExpanded ? 'Collapse' : 'Expand'}
              </Button>
            )}
          </div>
          {trial.searches && trial.searches.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Element</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Search Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Fee</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(isTrialFeesExpanded ? trial.searches : trial.searches.slice(0, 3)).map((search, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{search.level || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{search.element || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{search.searchName || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${search.entryFee || '0'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!isTrialFeesExpanded && trial.searches.length > 3 && (
                <div className="text-center py-2 bg-gray-50 border-t border-gray-200">
                  <span className="text-sm text-gray-500">
                    Showing 3 of {trial.searches.length} searches
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500 mb-2">No entry fees configured for this trial.</p>
              <p className="text-sm text-gray-400">Click "Edit Fees" to add searches and entry fees.</p>
            </div>
          )}
        </div>

        {/* Trial Entries by Search */}
        <div className="mb-6">
          <div className="flex items-center mb-3 border-b border-purple-100 pb-2">
            <h3 className="text-lg font-semibold text-purple-600">Trial Entries by Search</h3>
            {trial.searches && trial.searches.length > 3 && (
              <Button
                type="text"
                icon={isTrialEntriesExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setIsTrialEntriesExpanded(!isTrialEntriesExpanded)}
                style={{ color: '#5A1E66', marginLeft: 'auto' }}
              >
                {isTrialEntriesExpanded ? 'Collapse' : 'Expand'}
              </Button>
            )}
          </div>
          {entriesLoading ? (
            <div className="flex justify-center py-8">
              <Spin size="large" />
            </div>
          ) : trial.searches && trial.searches.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Search Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Element</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Fee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Entries</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(isTrialEntriesExpanded ? trial.searches : trial.searches.slice(0, 3)).map((search, index) => {
                    const entryCount = search.entryCount || 0;
                    return (
                      <tr key={index} className={entryCount > 0 ? 'hover:bg-gray-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{search.searchName || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{search.level || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{search.element || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${search.entryFee || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-purple-600">
                          {entryCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {entryCount > 0 ? (
                            <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                setSelectedSearch(search);
                                setIsEntriesModalVisible(true);
                              }}
                              style={{ color: '#5A1E66', padding: 0 }}
                            >
                              View Entries ({entryCount})
                            </Button>
                          ) : (
                            <span className="text-gray-400">No entries</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!isTrialEntriesExpanded && trial.searches.length > 3 && (
                <div className="text-center py-2 bg-gray-50 border-t border-gray-200">
                  <span className="text-sm text-gray-500">
                    Showing 3 of {trial.searches.length} searches • Total Entries: {trial.searches.reduce((sum, s) => sum + (s.entryCount || 0), 0)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-gray-500 mb-2">No searches configured for this trial.</p>
            </div>
          )}
        </div>

        {/* Approval Status */}
        <InfoSection title="Approval Status" onEdit={() => handleEditSection('approval')}>
          <InfoItem label="Current Approval Status" value={trial.trail?.approvalStatus} />
        </InfoSection>

        {/* Location Information */}
        <InfoSection title="Location Information" onEdit={() => handleEditSection('location')}>
          <InfoItem label="Location Name" value={trial.trail?.trialLocationName} fullWidth />
          <InfoItem label="Address" value={trial.trail?.trialLocationAddress} fullWidth />
          <InfoItem label="City" value={trial.trail?.city} />
          <InfoItem label="State" value={trial.trail?.state} />
          <InfoItem label="ZIP Code" value={trial.trail?.zip} />
          <InfoItem label="Timezone" value={trial.trail?.timezone} />
        </InfoSection>

        {/* Registration Dates */}
        <InfoSection title="Registration Dates" onEdit={() => handleEditSection('dates')}>
          <InfoItem
            label="Open Date"
            value={trial.trail?.openDate ? moment.utc(trial.trail.openDate).format('MM/DD/YYYY hh:mm A') : 'N/A'}
          />
          <InfoItem
            label="Close Date"
            value={trial.trail?.closeDate ? moment.utc(trial.trail.closeDate).format('MM/DD/YYYY hh:mm A') : 'N/A'}
          />
          <InfoItem
            label="Cutoff Date"
            value={trial.trail?.cutoffDate ? moment.utc(trial.trail.cutoffDate).format('MM/DD/YYYY hh:mm A') : 'N/A'}
          />
          <InfoItem
            label="Trial Date"
            value={trial.trail?.trialDate ? moment.utc(trial.trail.trialDate).format('MM/DD/YYYY hh:mm A') : 'N/A'}
          />
        </InfoSection>

        {/* Host Information */}
        <InfoSection title="Host Information">
          <InfoItem label="Host Name" value={trial.host?.hostName} fullWidth />
          <InfoItem label="Contact Address" value={trial.host?.contactAddress} fullWidth />
          <InfoItem label="City" value={trial.host?.city} />
          <InfoItem label="State" value={trial.host?.state} />
          <InfoItem label="ZIP Code" value={trial.host?.zip} />
          <InfoItem label="Phone Number" value={trial.host?.phoneNumber} />
          <InfoItem label="Trial Secretary Name" value={trial.host?.trialSecretaryName} />
          <InfoItem label="Trial Secretary Email" value={trial.host?.trialSecretaryEmail} />
          <InfoItem label="PayPal Email" value={trial.host?.paypalEmail} />
          <InfoItem label="PayPal Payment Link" value={trial.host?.paypalPaymentLink} fullWidth />
        </InfoSection>

        {/* Description & Notes */}
        <InfoSection title="Additional Information" onEdit={() => handleEditSection('additional')}>
          <InfoItem label="Description" value={trial.trail?.description} fullWidth />
          <InfoItem label="Notes" value={trial.trail?.notes} fullWidth />
          <InfoItem label="Trial File" value={trial.trail?.trialFile} fullWidth />
          <InfoItem label="Trial Images" value={trial.trail?.trialImages} fullWidth />
          <InfoItem label="Judge Auth ID" value={trial.trail?.judgeAuthId} fullWidth />
        </InfoSection>

      </div>

      {/* Edit Modal */}
      <Modal
        title={`Edit ${
          editSection === 'basic' ? 'Basic Information' :
          editSection === 'approval' ? 'Approval Status' :
          editSection === 'location' ? 'Location Information' :
          editSection === 'dates' ? 'Registration Dates' :
          'Additional Information'
        }`}
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => {
          setEditModalVisible(false);
          setEditSection(null);
        }}
        width={800}
        okText="Save"
        okButtonProps={{
          style: {
            backgroundColor: editSection === 'approval' ? '#10B981' : '#5A1E66',
            borderColor: editSection === 'approval' ? '#10B981' : '#5A1E66',
          }
        }}
      >
        <Form form={form} layout="vertical">
          {/* Basic Information Fields */}
          {editSection === 'basic' && (
            <>
              <Form.Item name="trialName" label="Trial Name">
                <Input />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="status" label="Status">
                  <Select placeholder="Select status">
                    <Select.Option value="Upcoming">Upcoming</Select.Option>
                    <Select.Option value="Open">Open</Select.Option>
                    <Select.Option value="Close">Close</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item name="noOfJudge" label="Number of Judges">
                  <Input type="number" />
                </Form.Item>
              </div>
              <Form.Item name="maxEntries" label="Max Entries">
                <Input type="number" />
              </Form.Item>
            </>
          )}

          {/* Approval Status Field */}
          {editSection === 'approval' && (
            <Form.Item name="approvalStatus" label="Approval Status">
              <Select placeholder="Select approval status" size="large">
                <Select.Option value="Pending">Pending</Select.Option>
                <Select.Option value="Approve">Approve</Select.Option>
                <Select.Option value="Reject">Reject</Select.Option>
              </Select>
            </Form.Item>
          )}

          {/* Location Information Fields */}
          {editSection === 'location' && (
            <>
              <Form.Item name="trialLocationName" label="Location Name">
                <Input />
              </Form.Item>
              <Form.Item name="trialLocationAddress" label="Address">
                <Input />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="city" label="City">
                  <Input />
                </Form.Item>
                <Form.Item name="state" label="State">
                  <Input />
                </Form.Item>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item name="zip" label="ZIP Code">
                  <Input />
                </Form.Item>
                <Form.Item name="timezone" label="Timezone">
                  <Input />
                </Form.Item>
              </div>
            </>
          )}

          {/* Registration Dates Fields */}
          {editSection === 'dates' && (
            <>
              <Form.Item name="trialDate" label="Trial Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="openDate" label="Open Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="closeDate" label="Close Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="cutoffDate" label="Cutoff Date">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          {/* Additional Information Fields */}
          {editSection === 'additional' && (
            <>
              <Form.Item name="description" label="Description">
                <Input.TextArea rows={4} />
              </Form.Item>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={4} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Edit Entry Fees Modal */}
      <Modal
        title={`Edit Entry Fees - ${trial.trail?.trialName || 'Trial'}`}
        open={isEditFeesModalVisible}
        onCancel={() => {
          setIsEditFeesModalVisible(false);
          setEditingSearches([]);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsEditFeesModalVisible(false);
              setEditingSearches([]);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Cancel
          </Button>,
          <Button
            key="add"
            onClick={handleAddSearch}
            style={{
              backgroundColor: '#10B981',
              borderColor: '#10B981',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            + Add Search
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSaveFees}
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Save All Changes
          </Button>,
        ]}
        width={900}
      >
        <div className="space-y-4">
          <Alert
            type="info"
            message="Entry Fees Management"
            description="Add, edit, or remove searches and their entry fees. Each trial can have multiple searches with different fee structures."
            showIcon
          />

          {editingSearches.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No searches configured for this trial.</p>
              <Button
                type="primary"
                onClick={handleAddSearch}
                style={{
                  backgroundColor: '#5A1E66',
                  borderColor: '#5A1E66',
                }}
              >
                + Add First Search
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {editingSearches.map((search, index) => (
                <div key={search.key} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-700">Search #{index + 1}</h4>
                    <Button
                      danger
                      size="small"
                      onClick={() => handleRemoveSearch(search.key)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Level <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={search.level}
                        onChange={(value) => handleSearchChange(search.key, 'level', value)}
                        style={{ width: '100%' }}
                        placeholder="Select level"
                      >
                        <Select.Option value="SIT">SIT</Select.Option>
                        <Select.Option value="Novice">Novice</Select.Option>
                        <Select.Option value="Advanced">Advanced</Select.Option>
                        <Select.Option value="Excellent">Excellent</Select.Option>
                        <Select.Option value="Premier">Premier</Select.Option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Element
                      </label>
                      <Input
                        value={search.element}
                        onChange={(e) => handleSearchChange(search.key, 'element', e.target.value)}
                        placeholder="e.g., Birch, Anise, Clove"
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Search Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={search.searchName}
                        onChange={(e) => handleSearchChange(search.key, 'searchName', e.target.value)}
                        placeholder="e.g., Container Search"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entry Fee ($) <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        value={search.entryFee}
                        onChange={(e) => handleSearchChange(search.key, 'entryFee', e.target.value)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* View Entries Modal */}
      <Modal
        title={
          <div>
            <h3 className="text-lg font-semibold text-purple-600">
              Entries for {selectedSearch?.searchName}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {selectedSearch?.level} - {selectedSearch?.element}
            </p>
          </div>
        }
        open={isEntriesModalVisible}
        onCancel={() => {
          setIsEntriesModalVisible(false);
          setSelectedSearch(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsEntriesModalVisible(false);
              setSelectedSearch(null);
            }}
          >
            Close
          </Button>
        ]}
        width={900}
      >
        {entriesLoading ? (
          <div className="flex justify-center py-8">
            <Spin size="large" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dog Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Division</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handler</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entriesData?.getRegisteredDogsTrialByTrialCalendar?.data
                  ?.filter(entry => entry.elements === selectedSearch?.searchName)
                  .map((entry, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.dog || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {entry.division || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {entry.memberName || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {entry.email || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {entry.phoneNumber || 'N/A'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {entriesData?.getRegisteredDogsTrialByTrialCalendar?.data?.filter(
              entry => entry.elements === selectedSearch?.searchName
            ).length === 0 && (
              <div className="text-center py-8 bg-gray-50">
                <p className="text-gray-500">No entries found for this search.</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Back Button at Bottom */}
      <div className="mt-8 pt-6 border-t">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/trials')}
          size="large"
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Trials
        </Button>
      </div>
    </div>
  );
};

// User Detail View Component
const UserDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authId = decodeURIComponent(location.pathname.split('/').pop());
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [form] = Form.useForm();

  // Scroll to top when component mounts or authId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [authId]);

  const { loading, error, data, refetch } = useQuery(GET_MEMBER_BY_AUTH_ID, {
    variables: { authId: authId },
  });

  const [updateUser] = useMutation(UPDATE_USER_BY_ADMIN, {
    onCompleted: () => {
      message.success('User updated successfully!');
      setEditModalVisible(false);
      setEditSection(null);
      refetch();
    },
    onError: (err) => {
      let errorMessage = err.message;
      if (errorMessage.includes('emergency_contact')) {
        errorMessage = 'Contact name/number required';
      }
      message.error(`Failed to update user: ${errorMessage}`);
    },
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  const user = data?.getCommonUserDetail;
  if (!user) return (
    <div className="p-6">
      <Alert message="User Not Found" description="The requested user could not be found." type="warning" showIcon />
    </div>
  );

  const handleEdit = (section) => {
    setEditSection(section);
    const nameParts = user.name?.split(' ') || [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    if (section === 'basic') {
      form.setFieldsValue({
        first_name: firstName,
        last_name: lastName,
        email: user.email,
        phone_number: user.phone_number,
      });
    } else if (section === 'address') {
      form.setFieldsValue({
        location_address: user.location_address,
        city: user.city,
        state: user.state,
        zipCode: user.zipCode,
        timezone: user.timezone,
      });
    } else if (section === 'emergency') {
      form.setFieldsValue({
        emergency_contact_name: user.emergency_contact_name,
        emergency_contact_number: user.emergency_contact_number,
      });
    } else if (section === 'additional') {
      form.setFieldsValue({
        website: user.website,
        distance_travel: user.distance_travel,
        profile: user.profile,
      });
    } else if (section === 'roles') {
      form.setFieldsValue({
        role: user.role || [],
        level: user.level || [],
        notes: user.notes,
      });
    }

    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      const nameParts = user.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const cleanedValues = Object.keys(values).reduce((acc, key) => {
        const value = values[key];
        acc[key] = (typeof value === 'string' && value.trim() === '') ? null : value;
        return acc;
      }, {});

      const updateData = {
        first_name: cleanedValues.first_name || firstName,
        last_name: cleanedValues.last_name || lastName,
        ...cleanedValues,
      };

      await updateUser({
        variables: {
          authId: user.authId,
          data: updateData,
        },
      });
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const InfoSection = ({ title, children, onEdit }) => (
    <div className="mb-8">
      <div className="flex items-center mb-4 pb-2 border-b-2 border-purple-600" style={{ gap: '20px' }}>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {onEdit && (
          <Button
            icon={<EditOutlined />}
            onClick={onEdit}
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: 'white',
            }}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );

  const InfoItem = ({ label, value, fullWidth = false }) => (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-base text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title={`User Details - ${user.name || 'Unknown'}`}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/users')}
          size="large"
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Users
        </Button>
      </PageInfo>

      <div className="space-y-6">
        <InfoSection title="Basic Information" onEdit={() => handleEdit('basic')}>
          <InfoItem label="User ID" value={user.id} />
          <InfoItem label="Auth ID" value={user.authId} />
          <InfoItem label="Name" value={user.name} />
          <InfoItem label="Email" value={user.email} />
          <InfoItem label="Phone Number" value={user.phone_number} />
          <InfoItem label="Payment Status" value={user.mark_as_paid ? 'Paid' : 'Not Paid'} />
          <InfoItem label="Account Status" value={user.isBlocked ? 'Blocked' : 'Active'} />
          <InfoItem label="Created At" value={user.createdAt ? new Date(user.createdAt).toLocaleString() : 'N/A'} />
        </InfoSection>

        <InfoSection title="Address Information" onEdit={() => handleEdit('address')}>
          <InfoItem label="Street Address" value={user.location_address} fullWidth />
          <InfoItem label="City" value={user.city} />
          <InfoItem label="State" value={user.state} />
          <InfoItem label="ZIP Code" value={user.zipCode} />
          <InfoItem label="Timezone" value={user.timezone} />
        </InfoSection>

        <InfoSection title="Emergency Contact" onEdit={() => handleEdit('emergency')}>
          <InfoItem label="Contact Name" value={user.emergency_contact_name} />
          <InfoItem label="Contact Phone" value={user.emergency_contact_number} />
        </InfoSection>

        <InfoSection title="Additional Information" onEdit={() => handleEdit('additional')}>
          <InfoItem label="Website" value={user.website} fullWidth />
          <InfoItem label="Distance Willing to Travel" value={user.distance_travel} fullWidth />
          <InfoItem label="Profile Image" value={user.profile} fullWidth />
        </InfoSection>

        <InfoSection title="Roles & Judge Information" onEdit={() => handleEdit('roles')}>
          <InfoItem label="Roles" value={user.role?.join(', ')} />
          <InfoItem label="Judge Levels" value={user.level?.join(', ')} />
          <InfoItem label="Number of Trials Judged" value={user.noOfTrialsJudged} />
          <InfoItem label="Number of Trials Participated" value={user.noOfTrialsParticipated} />
          <InfoItem label="Number of Trials Hosted" value={user.noofTrialsHosted} />
          <InfoItem label="Number of Upcoming Trials" value={user.noOfUpcomingTrials} />
          <InfoItem label="Admin Notes" value={user.notes} fullWidth />
        </InfoSection>
      </div>

      <div className="mt-8 pt-6 border-t">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/users')}
          size="large"
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Users
        </Button>
      </div>

      <Modal
        title={`Edit ${editSection === 'basic' ? 'Basic Information' :
                editSection === 'address' ? 'Address Information' :
                editSection === 'emergency' ? 'Emergency Contact' :
                editSection === 'additional' ? 'Additional Information' :
                editSection === 'roles' ? 'Roles & Judge Information' :
                'Information'}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditSection(null);
          form.resetFields();
        }}
        onOk={handleUpdate}
        okText="Save"
        okButtonProps={{
          style: {
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
          }
        }}
      >
        <Form form={form} layout="vertical">
          {editSection === 'basic' && (
            <>
              <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="phone_number" label="Phone Number" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'address' && (
            <>
              <Form.Item name="location_address" label="Street Address">
                <Input />
              </Form.Item>
              <Form.Item name="city" label="City">
                <Input />
              </Form.Item>
              <Form.Item name="state" label="State">
                <Input />
              </Form.Item>
              <Form.Item name="zipCode" label="ZIP Code">
                <Input />
              </Form.Item>
              <Form.Item name="timezone" label="Timezone">
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'emergency' && (
            <>
              <Form.Item name="emergency_contact_name" label="Emergency Contact Name">
                <Input />
              </Form.Item>
              <Form.Item name="emergency_contact_number" label="Emergency Contact Phone">
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'additional' && (
            <>
              <Form.Item name="website" label="Website">
                <Input />
              </Form.Item>
              <Form.Item name="distance_travel" label="Distance Willing to Travel">
                <Input />
              </Form.Item>
              <Form.Item name="profile" label="Profile Image URL">
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'roles' && (
            <>
              <Form.Item name="role" label="Roles">
                <Select mode="multiple" placeholder="Select roles">
                  <Select.Option value="Member">Member</Select.Option>
                  <Select.Option value="Judge">Judge</Select.Option>
                  <Select.Option value="Host">Host</Select.Option>
                  <Select.Option value="Super Admin">Super Admin</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="level" label="Judge Levels">
                <Select mode="multiple" placeholder="Select judge levels">
                  <Select.Option value="SIT">SIT</Select.Option>
                  <Select.Option value="Novice">Novice</Select.Option>
                  <Select.Option value="Advanced">Advanced</Select.Option>
                  <Select.Option value="Excellent">Excellent</Select.Option>
                  <Select.Option value="Premier">Premier</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="notes" label="Admin Notes">
                <Input.TextArea rows={4} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

// Member Detail View Component
const MemberDetailView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authId = decodeURIComponent(location.pathname.split('/').pop());
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [form] = Form.useForm();

  // Scroll to top when component mounts or authId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [authId]);

  const { loading, error, data, refetch } = useQuery(GET_MEMBER_BY_AUTH_ID, {
    variables: { authId: authId },
  });

  const [updateMember] = useMutation(UPDATE_USER_BY_ADMIN, {
    onCompleted: () => {
      message.success('Member updated successfully!');
      setEditModalVisible(false);
      setEditSection(null);
      refetch();
    },
    onError: (err) => {
      // Show user-friendly error messages
      let errorMessage = err.message;

      if (errorMessage.includes('emergency_contact')) {
        errorMessage = 'Contact name/number required';
      }

      message.error(`Failed to update member: ${errorMessage}`);
    },
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  const member = data?.getCommonUserDetail;
  if (!member) return (
    <div className="p-6">
      <Alert message="Member Not Found" description="The requested member could not be found." type="warning" showIcon />
    </div>
  );

  const handleEdit = (section) => {
    setEditSection(section);

    // Split name into first and last name
    const nameParts = member.name?.split(' ') || [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    // Populate form based on section
    if (section === 'basic') {
      form.setFieldsValue({
        first_name: firstName,
        last_name: lastName,
        email: member.email,
        phone_number: member.phone_number,
      });
    } else if (section === 'address') {
      form.setFieldsValue({
        location_address: member.location_address,
        city: member.city,
        state: member.state,
        zipCode: member.zipCode,
        timezone: member.timezone,
      });
    } else if (section === 'emergency') {
      form.setFieldsValue({
        emergency_contact_name: member.emergency_contact_name,
        emergency_contact_number: member.emergency_contact_number,
      });
    } else if (section === 'additional') {
      form.setFieldsValue({
        website: member.website,
        distance_travel: member.distance_travel,
        profile: member.profile,
      });
    } else if (section === 'roles') {
      form.setFieldsValue({
        role: member.role || [],
        level: member.level || [],
        notes: member.notes,
      });
    }

    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      console.log('🔍 Form values before cleaning:', values);

      // Split name into first and last name for the mutation
      const nameParts = member.name?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Convert empty strings to null so they can be cleared in the database
      const cleanedValues = Object.keys(values).reduce((acc, key) => {
        const value = values[key];
        // Convert empty strings to null, keep arrays and other values as-is
        acc[key] = (typeof value === 'string' && value.trim() === '') ? null : value;
        return acc;
      }, {});

      console.log('🧹 Cleaned values:', cleanedValues);

      // Always include required fields (first_name, last_name) along with edited values
      const updateData = {
        first_name: cleanedValues.first_name || firstName,
        last_name: cleanedValues.last_name || lastName,
        ...cleanedValues,
      };

      console.log('📤 Sending to backend:', updateData);

      await updateMember({
        variables: {
          authId: member.authId,
          data: updateData,
        },
      });
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const InfoSection = ({ title, children, onEdit }) => (
    <div className="mb-8">
      <div className="flex items-center mb-4 pb-2 border-b-2 border-purple-600" style={{ gap: '20px' }}>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {onEdit && (
          <Button
            icon={<EditOutlined />}
            onClick={onEdit}
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: 'white',
            }}
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {children}
      </div>
    </div>
  );

  const InfoItem = ({ label, value, fullWidth = false }) => (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-base text-gray-900">{value || 'N/A'}</p>
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      {/* Header with Title and Back Button */}
      <PageInfo title={`Member Details - ${member.name || 'Unknown'}`}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/members')}
          size="large"
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Members
        </Button>
      </PageInfo>

      {/* Member Information Sections */}
      <div className="space-y-6">

        {/* Basic Information */}
        <InfoSection title="Basic Information" onEdit={() => handleEdit('basic')}>
          <InfoItem label="Member ID" value={member.id} />
          <InfoItem label="Auth ID" value={member.authId} />
          <InfoItem label="Name" value={member.name} />
          <InfoItem label="Email" value={member.email} />
          <InfoItem label="Phone Number" value={member.phone_number} />
          <InfoItem label="Payment Status" value={member.mark_as_paid ? 'Paid' : 'Not Paid'} />
          <InfoItem label="Account Status" value={member.isBlocked ? 'Blocked' : 'Active'} />
          <InfoItem label="Created At" value={member.createdAt ? new Date(member.createdAt).toLocaleString() : 'N/A'} />
        </InfoSection>

        {/* Address Information */}
        <InfoSection title="Address Information" onEdit={() => handleEdit('address')}>
          <InfoItem label="Street Address" value={member.location_address} fullWidth />
          <InfoItem label="City" value={member.city} />
          <InfoItem label="State" value={member.state} />
          <InfoItem label="ZIP Code" value={member.zipCode} />
          <InfoItem label="Timezone" value={member.timezone} />
        </InfoSection>

        {/* Emergency Contact */}
        <InfoSection title="Emergency Contact" onEdit={() => handleEdit('emergency')}>
          <InfoItem label="Contact Name" value={member.emergency_contact_name} />
          <InfoItem label="Contact Phone" value={member.emergency_contact_number} />
        </InfoSection>

        {/* Additional Information */}
        <InfoSection title="Additional Information" onEdit={() => handleEdit('additional')}>
          <InfoItem label="Website" value={member.website} fullWidth />
          <InfoItem label="Distance Willing to Travel" value={member.distance_travel} fullWidth />
          <InfoItem label="Profile Image" value={member.profile} fullWidth />
        </InfoSection>

        {/* Roles & Judge Information */}
        <InfoSection title="Roles & Judge Information" onEdit={() => handleEdit('roles')}>
          <InfoItem label="Roles" value={member.role?.join(', ')} />
          <InfoItem label="Judge Levels" value={member.level?.join(', ')} />
          <InfoItem label="Number of Trials Judged" value={member.noOfTrialsJudged} />
          <InfoItem label="Admin Notes" value={member.notes} fullWidth />
        </InfoSection>

      </div>

      {/* Back Button at Bottom */}
      <div className="mt-8 pt-6 border-t">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/database/members')}
          size="large"
          style={{
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
            color: '#fff'
          }}
        >
          Back to Members
        </Button>
      </div>

      {/* Edit Modal */}
      <Modal
        title={`Edit ${editSection === 'basic' ? 'Basic Information' :
                editSection === 'address' ? 'Address Information' :
                editSection === 'emergency' ? 'Emergency Contact' :
                editSection === 'additional' ? 'Additional Information' :
                editSection === 'roles' ? 'Roles & Judge Information' :
                'Admin Notes'}`}
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setEditSection(null);
          form.resetFields();
        }}
        onOk={handleUpdate}
        okText="Save"
        okButtonProps={{
          style: {
            backgroundColor: '#5A1E66',
            borderColor: '#5A1E66',
          }
        }}
      >
        <Form form={form} layout="vertical">
          {editSection === 'basic' && (
            <>
              <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="phone_number" label="Phone Number" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'address' && (
            <>
              <Form.Item name="location_address" label="Street Address">
                <Input />
              </Form.Item>
              <Form.Item name="city" label="City">
                <Input />
              </Form.Item>
              <Form.Item name="state" label="State">
                <Input />
              </Form.Item>
              <Form.Item name="zipCode" label="ZIP Code">
                <Input />
              </Form.Item>
              <Form.Item name="timezone" label="Timezone">
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'emergency' && (
            <>
              <Form.Item name="emergency_contact_name" label="Emergency Contact Name">
                <Input />
              </Form.Item>
              <Form.Item name="emergency_contact_number" label="Emergency Contact Phone">
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'additional' && (
            <>
              <Form.Item name="website" label="Website">
                <Input />
              </Form.Item>
              <Form.Item name="distance_travel" label="Distance Willing to Travel">
                <Input />
              </Form.Item>
              <Form.Item name="profile" label="Profile Image URL">
                <Input />
              </Form.Item>
            </>
          )}
          {editSection === 'roles' && (
            <>
              <Form.Item name="role" label="Roles">
                <Select
                  mode="multiple"
                  placeholder="Select roles"
                  style={{ width: '100%' }}
                >
                  <Select.Option value="Member">Member</Select.Option>
                  <Select.Option value="Host">Host</Select.Option>
                  <Select.Option value="Judge">Judge</Select.Option>
                  <Select.Option value="Admin">Admin</Select.Option>
                  <Select.Option value="Super Admin">Super Admin</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="level" label="Judge Levels">
                <Select
                  mode="multiple"
                  placeholder="Select judge levels"
                  style={{ width: '100%' }}
                >
                  <Select.Option value="SIT">SIT</Select.Option>
                  <Select.Option value="Novice">Novice</Select.Option>
                  <Select.Option value="Advanced">Advanced</Select.Option>
                  <Select.Option value="Excellent">Excellent</Select.Option>
                  <Select.Option value="Premier">Premier</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="notes" label="Admin Notes">
                <Input.TextArea rows={4} />
              </Form.Item>
            </>
          )}
          {editSection === 'notes' && (
            <Form.Item name="notes" label="Admin Notes">
              <Input.TextArea rows={4} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

// Trials List Component
const TrialsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [editingTrial, setEditingTrial] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isEditFeesModalVisible, setIsEditFeesModalVisible] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [editingSearches, setEditingSearches] = useState([]);
  const [form] = Form.useForm();
  const [feesForm] = Form.useForm();

  // Fetch ALL trials at once
  const { loading, error, data, refetch } = useQuery(GET_ALL_TRIALS, {
    variables: { pageNumber: 1, pageSize: 10000, search: null, approvalStatus: null, role: null },
  });

  const [updateTrial, { loading: updateLoading }] = useMutation(UPDATE_TRIAL_BY_ADMIN, {
    onCompleted: () => {
      message.success('Trial updated successfully');
      setIsEditModalVisible(false);
      setEditingTrial(null);
      form.resetFields();
      refetch();
    },
    onError: (error) => {
      message.error(`Failed to update trial: ${error.message}`);
    },
  });

  const [deleteTrial, { loading: deleteLoading }] = useMutation(DELETE_TRIAL_BY_ADMIN, {
    onCompleted: () => {
      message.success('Trial deleted successfully');
      setIsDeleteModalVisible(false);
      setEditingTrial(null);
      setDeleteConfirmName('');
      refetch();
    },
    onError: (error) => {
      message.error(`Failed to delete trial: ${error.message}`);
    },
  });

  // Sort by trial date descending (newest first)
  const sortedData = data?.getAllTrialCalendar?.data
    ? [...data.getAllTrialCalendar.data].sort((a, b) => new Date(b.trial?.trialDate) - new Date(a.trial?.trialDate))
    : [];

  // Client-side filtering based on search term and filters
  const filteredData = sortedData.filter(trial => {
    // Search term filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        trial.trial?.trialName?.toLowerCase().includes(searchLower) ||
        trial.trial?.city?.toLowerCase().includes(searchLower) ||
        trial.trial?.state?.toLowerCase().includes(searchLower) ||
        trial.host?.hostName?.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (trial.trial?.status !== statusFilter) return false;
    }

    // Approval filter
    if (approvalFilter !== 'all') {
      if (trial.trial?.approvalStatus !== approvalFilter) return false;
    }

    return true;
  });

  const handleEdit = (record) => {
    setEditingTrial(record);
    form.setFieldsValue({
      trialName: record.trial?.trialName || '',
      trialLocationName: record.trial?.trialLocationName || '',
      trialLocationAddress: record.trial?.trialLocationAddress || '',
      city: record.trial?.city || '',
      state: record.trial?.state || '',
      zip: record.trial?.zip || '',
      timezone: record.trial?.timezone || '',
      // Convert dates to moment objects for Ant Design DatePicker
      // This matches how the existing app handles dates
      trialDate: record.trial?.trialDate ? moment.utc(record.trial.trialDate) : null,
      openDate: record.trial?.openDate ? moment.utc(record.trial.openDate) : null,
      closeDate: record.trial?.closeDate ? moment.utc(record.trial.closeDate) : null,
      cutoffDate: record.trial?.cutoffDate ? moment.utc(record.trial.cutoffDate) : null,
      status: record.trial?.status || 'Upcoming',
      approvalStatus: record.trial?.approvalStatus || 'Pending',
      maxEntries: record.trial?.maxEntries || null,
      description: record.trial?.description || '',
      notes: record.trial?.notes || '',
    });
    setIsEditModalVisible(true);
  };

  const handleUpdate = async () => {
    try {
      // Get ALL form values (not just from current tab)
      // This ensures dates are included even when saving from "Basic Info" tab
      const values = form.getFieldsValue();

      // Prepare the update data with the full structure required by the backend
      // Note: IDs must be integers, approvalStatus is NOT in UpdateTrialInput (has separate mutation)
      // Note: authId should NOT be sent (backend doesn't accept it in update)

      // Ensure searches array is properly formatted with required fields
      // Backend returns 'search' (singular) not 'searches' (plural)
      const searches = Array.isArray(editingTrial.search)
        ? editingTrial.search.map(search => ({
            level: search.level || '',
            element: search.element || null,
            searchName: search.searchName || '',
            entryFee: search.entryFee || null,
          }))
        : [];

      const updateData = {
        host: {
          id: parseInt(editingTrial.host?.id, 10),
          hostName: editingTrial.host?.hostName || '',
          contactAddress: editingTrial.host?.contactAddress || '',
          city: editingTrial.host?.city || '',
          trialSecretaryName: editingTrial.host?.trialSecretaryName || '',
          state: editingTrial.host?.state || '',
          trialSecretaryEmail: editingTrial.host?.trialSecretaryEmail || '',
          zip: editingTrial.host?.zip || '',
          phoneNumber: editingTrial.host?.phoneNumber || '',
          paypalEmail: editingTrial.host?.paypalEmail || '',
          paypalPaymentLink: editingTrial.host?.paypalPaymentLink || '',
        },
        trial: {
          id: parseInt(editingTrial.trial?.id, 10),
          trialName: values.trialName || editingTrial.trial?.trialName || '',
          trialLocationName: values.trialLocationName || editingTrial.trial?.trialLocationName || '',
          trialLocationAddress: values.trialLocationAddress || editingTrial.trial?.trialLocationAddress || '',
          city: values.city || editingTrial.trial?.city || '',
          state: values.state || editingTrial.trial?.state || '',
          zip: values.zip || editingTrial.trial?.zip || '',
          timezone: values.timezone || editingTrial.trial?.timezone || '',
          // Convert moment objects to ISO strings - EXACT MATCH to existing app (SearchDetails/index.js line 325-327)
          trialDate: values.trialDate
            ? (values.trialDate instanceof Date ? values.trialDate.toISOString() : new Date(values.trialDate).toISOString())
            : (editingTrial.trial?.trialDate ? new Date(editingTrial.trial.trialDate).toISOString() : null),
          openDate: values.openDate
            ? (values.openDate instanceof Date ? values.openDate.toISOString() : new Date(values.openDate).toISOString())
            : (editingTrial.trial?.openDate ? new Date(editingTrial.trial.openDate).toISOString() : null),
          closeDate: values.closeDate
            ? (values.closeDate instanceof Date ? values.closeDate.toISOString() : new Date(values.closeDate).toISOString())
            : (editingTrial.trial?.closeDate ? new Date(editingTrial.trial.closeDate).toISOString() : null),
          cutoffDate: values.cutoffDate
            ? (values.cutoffDate instanceof Date ? values.cutoffDate.toISOString() : new Date(values.cutoffDate).toISOString())
            : (editingTrial.trial?.cutoffDate ? new Date(editingTrial.trial.cutoffDate).toISOString() : null),
          trialFile: editingTrial.trial?.trialFile || '',
          description: values.description || editingTrial.trial?.description || '',
          notes: values.notes || editingTrial.trial?.notes || '',
          status: values.status || editingTrial.trial?.status || 'Upcoming',
          // approvalStatus is NOT included - it has a separate mutation (updateTrialApprovalStatus)
          noOfJudge: editingTrial.trial?.noOfJudge || null,
          trialFees: null, // Trial fees are unused - entry fees are in searches array
          maxEntries: values.maxEntries ? parseInt(values.maxEntries, 10) : (editingTrial.trial?.maxEntries || null),
          trialImages: editingTrial.trial?.trialImages || '',
          judgeAuthId: editingTrial.trial?.judgeAuthId || [],
        },
        searches: searches,
      };

      await updateTrial({
        variables: {
          trialCalendarId: parseInt(editingTrial.id, 10),
          data: updateData,
        },
      });
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const handleEditFees = (record) => {
    setEditingTrial(record);
    // Initialize the searches array for editing (backend returns 'search' not 'searches')
    const searches = Array.isArray(record.search) ? record.search : [];
    setEditingSearches(searches.map((search, index) => ({
      key: index,
      id: search.id,
      level: search.level || '',
      element: search.element || '',
      searchName: search.searchName || '',
      entryFee: search.entryFee || 0,
    })));
    setIsEditFeesModalVisible(true);
  };

  const handleAddSearch = () => {
    const newSearch = {
      key: editingSearches.length,
      id: null, // New search, no ID yet
      level: '',
      element: '',
      searchName: '',
      entryFee: 0,
    };
    setEditingSearches([...editingSearches, newSearch]);
  };

  const handleRemoveSearch = (key) => {
    setEditingSearches(editingSearches.filter(search => search.key !== key));
  };

  const handleSearchChange = (key, field, value) => {
    setEditingSearches(editingSearches.map(search =>
      search.key === key ? { ...search, [field]: value } : search
    ));
  };

  const handleSaveFees = async () => {
    try {
      // Validate that all searches have required fields
      const invalidSearches = editingSearches.filter(s =>
        !s.level || !s.searchName || s.entryFee <= 0
      );

      if (invalidSearches.length > 0) {
        message.error('All searches must have a level, search name, and entry fee greater than 0');
        return;
      }

      // Prepare the update data with the full structure required by the backend
      const searches = editingSearches.map(search => ({
        level: search.level,
        element: search.element || null,
        searchName: search.searchName,
        entryFee: parseInt(search.entryFee, 10),
      }));

      // Use the EXACT same structure as handleUpdate to ensure compatibility
      const updateData = {
        host: {
          id: parseInt(editingTrial.host?.id, 10),
          hostName: editingTrial.host?.hostName || '',
          contactAddress: editingTrial.host?.contactAddress || '',
          city: editingTrial.host?.city || '',
          trialSecretaryName: editingTrial.host?.trialSecretaryName || '',
          state: editingTrial.host?.state || '',
          trialSecretaryEmail: editingTrial.host?.trialSecretaryEmail || '',
          zip: editingTrial.host?.zip || '',
          phoneNumber: editingTrial.host?.phoneNumber || '',
          paypalEmail: editingTrial.host?.paypalEmail || '',
          paypalPaymentLink: editingTrial.host?.paypalPaymentLink || '',
        },
        trial: {
          id: parseInt(editingTrial.trial?.id, 10),
          trialName: editingTrial.trial?.trialName || '',
          trialLocationName: editingTrial.trial?.trialLocationName || '',
          trialLocationAddress: editingTrial.trial?.trialLocationAddress || '',
          city: editingTrial.trial?.city || '',
          state: editingTrial.trial?.state || '',
          zip: editingTrial.trial?.zip || '',
          timezone: editingTrial.trial?.timezone || '',
          trialDate: editingTrial.trial?.trialDate ? new Date(editingTrial.trial.trialDate).toISOString() : null,
          openDate: editingTrial.trial?.openDate ? new Date(editingTrial.trial.openDate).toISOString() : null,
          closeDate: editingTrial.trial?.closeDate ? new Date(editingTrial.trial.closeDate).toISOString() : null,
          cutoffDate: editingTrial.trial?.cutoffDate ? new Date(editingTrial.trial.cutoffDate).toISOString() : null,
          trialFile: editingTrial.trial?.trialFile || '',
          description: editingTrial.trial?.description || '',
          notes: editingTrial.trial?.notes || '',
          status: editingTrial.trial?.status || 'Upcoming',
          noOfJudge: editingTrial.trial?.noOfJudge || null,
          trialFees: null, // Trial fees are unused - entry fees are in searches array
          maxEntries: editingTrial.trial?.maxEntries || null,
          trialImages: editingTrial.trial?.trialImages || '',
          judgeAuthId: editingTrial.trial?.judgeAuthId || [],
        },
        searches: searches, // Updated searches array with new entry fees
      };

      console.log('Sending update with data:', JSON.stringify(updateData, null, 2));

      await updateTrial({
        variables: {
          trialCalendarId: parseInt(editingTrial.id, 10),
          data: updateData,
        },
      });

      message.success('Entry fees updated successfully!');
      refetch(); // Refresh the trials list
      setIsEditFeesModalVisible(false);
      setEditingSearches([]);
    } catch (err) {
      console.error('Failed to save fees - Full error:', err);
      console.error('GraphQL errors:', err.graphQLErrors);
      console.error('Network error:', err.networkError);
      message.error(`Failed to update trial: ${err.message}`);
    }
  };

  const handleDelete = (record) => {
    setEditingTrial(record);
    setDeleteConfirmName('');
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmName === editingTrial.trial?.trialName) {
      await deleteTrial({
        variables: {
          trialCalendarId: parseInt(editingTrial.id),
        },
      });
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
    },
    {
      title: 'Trial Name',
      dataIndex: ['trial', 'trialName'],
      key: 'trialName',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Trial Date',
      dataIndex: ['trial', 'trialDate'],
      key: 'trialDate',
      width: 120,
      render: (date) => date ? moment(date).format('MM/DD/YYYY') : 'N/A'
    },
    {
      title: 'Host',
      dataIndex: ['host', 'hostName'],
      key: 'hostName',
      width: 150,
    },
    {
      title: 'Judges',
      dataIndex: ['trial', 'noOfJudge'],
      key: 'noOfJudge',
      width: 80,
      render: (num) => num || '0'
    },
    {
      title: 'Total Entries',
      key: 'totalEntries',
      width: 120,
      render: (_, record) => {
        // Sum up all entry counts from all searches
        const total = record.search?.reduce((sum, search) => sum + (search.entryCount || 0), 0) || 0;
        return total;
      }
    },
    {
      title: 'Location',
      dataIndex: ['trial', 'city'],
      key: 'city',
      width: 130,
    },
    {
      title: 'State',
      dataIndex: ['trial', 'state'],
      key: 'state',
      width: 120,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 112,
      fixed: 'right',
      render: (_, record) => {
        return (
          <Space size="small" wrap>
            <Button
              type="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(record);
              }}
              style={{
                backgroundColor: '#5A1E66',
                borderColor: '#5A1E66',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Edit
            </Button>
            <Button
              danger
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(record);
              }}
              style={{
                backgroundColor: '#dc2626',
                borderColor: '#dc2626',
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Delete
            </Button>
          </Space>
        );
      },
    },
  ];

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen">
      <Spin size="large" />
    </div>
  );

  if (error) return (
    <div className="p-6">
      <Alert message="Error" description={error.message} type="error" showIcon />
    </div>
  );

  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title="Database Admin - Trials" />

      {/* Header */}
      <div className="flex justify-between items-center mt-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Trials ({filteredData.length} total)
        </h2>
      </div>

      {/* Filters Section */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <Search
              placeholder="Search by name, city, state, or host..."
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              options={[
                { value: 'all', label: 'All Statuses' },
                { value: 'Upcoming', label: 'Upcoming' },
                { value: 'Open', label: 'Open' },
                { value: 'Close', label: 'Closed' },
              ]}
            />
          </div>

          {/* Approval Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Approval Status</label>
            <Select
              value={approvalFilter}
              onChange={setApprovalFilter}
              style={{ width: '100%' }}
              options={[
                { value: 'all', label: 'All Approvals' },
                { value: 'Pending', label: 'Pending' },
                { value: 'Approved', label: 'Approved' },
                { value: 'Reject', label: 'Rejected' },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={false}
          loading={loading}
          className="border border-purple-100 rounded-lg"
          scroll={{ x: 1112 }}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/database/trials/${record.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      {/* Edit Trial Modal */}
      <Modal
        title="Edit Trial"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingTrial(null);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsEditModalVisible(false);
              setEditingTrial(null);
              form.resetFields();
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={updateLoading}
            onClick={handleUpdate}
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Save
          </Button>,
        ]}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="Basic Info" key="1">
              <Form.Item label="Trial Name" name="trialName">
                <Input />
              </Form.Item>
              <Form.Item label="Trial Location Name" name="trialLocationName">
                <Input />
              </Form.Item>
              <Form.Item label="Trial Location Address" name="trialLocationAddress">
                <Input />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <Form.Item label="City" name="city">
                  <Input />
                </Form.Item>
                <Form.Item label="State" name="state">
                  <Input />
                </Form.Item>
                <Form.Item label="Zip" name="zip">
                  <Input />
                </Form.Item>
              </div>
              <Form.Item label="Timezone" name="timezone">
                <Select>
                  <Select.Option value="EasternTime">Eastern Time</Select.Option>
                  <Select.Option value="CentralTime">Central Time</Select.Option>
                  <Select.Option value="MountainTime">Mountain Time</Select.Option>
                  <Select.Option value="PacificTime">Pacific Time</Select.Option>
                  <Select.Option value="AlaskaTime">Alaska Time</Select.Option>
                  <Select.Option value="Hawaii-AleutianTime">Hawaii-Aleutian Time</Select.Option>
                </Select>
              </Form.Item>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Dates & Status" key="2">
              <Form.Item label="Trial Date" name="trialDate">
                <DatePicker style={{ width: '100%' }} format="MMM DD, YYYY" />
              </Form.Item>
              <Form.Item label="Open Date" name="openDate">
                <DatePicker style={{ width: '100%' }} format="MMM DD, YYYY" />
              </Form.Item>
              <Form.Item label="Close Date" name="closeDate">
                <DatePicker style={{ width: '100%' }} format="MMM DD, YYYY" />
              </Form.Item>
              <Form.Item label="Cutoff Date" name="cutoffDate">
                <DatePicker style={{ width: '100%' }} format="MMM DD, YYYY" />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <Form.Item label="Status" name="status">
                  <Select>
                    <Select.Option value="Upcoming">Upcoming</Select.Option>
                    <Select.Option value="Open">Open</Select.Option>
                    <Select.Option value="Close">Close</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Approval Status (Read-Only)"
                  name="approvalStatus"
                  tooltip="Approval status requires a separate mutation and cannot be changed here"
                >
                  <Input disabled />
                </Form.Item>
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab="Details & Notes" key="3">
              <Form.Item label="Max Entries" name="maxEntries">
                <Input type="number" placeholder="Maximum number of entries allowed" />
              </Form.Item>
              <Form.Item label="Description" name="description">
                <Input.TextArea rows={4} placeholder="Trial description visible to users" />
              </Form.Item>
              <Form.Item label="Notes (Admin Only)" name="notes">
                <Input.TextArea rows={4} placeholder="Internal notes (not visible to users)" />
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Trial"
        open={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false);
          setEditingTrial(null);
          setDeleteConfirmName('');
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsDeleteModalVisible(false);
              setEditingTrial(null);
              setDeleteConfirmName('');
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            danger
            loading={deleteLoading}
            onClick={handleConfirmDelete}
            disabled={deleteConfirmName !== editingTrial?.trial?.trialName}
            style={{
              backgroundColor: deleteConfirmName === editingTrial?.trial?.trialName ? '#dc2626' : undefined,
              borderColor: deleteConfirmName === editingTrial?.trial?.trialName ? '#dc2626' : undefined,
              color: deleteConfirmName === editingTrial?.trial?.trialName ? '#fff' : undefined,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Delete
          </Button>,
        ]}
      >
        <div className="space-y-4">
          <Alert
            message="Warning"
            description="This action will soft-delete the trial. This cannot be undone."
            type="warning"
            showIcon
          />
          <p className="text-gray-700">
            To confirm deletion, please type the trial name exactly: <strong>{editingTrial?.trial?.trialName}</strong>
          </p>
          <Input
            placeholder="Type trial name to confirm"
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
          />
        </div>
      </Modal>

      {/* Edit Entry Fees Modal */}
      <Modal
        title={`Edit Entry Fees - ${editingTrial?.trial?.trialName || 'Trial'}`}
        open={isEditFeesModalVisible}
        onCancel={() => {
          setIsEditFeesModalVisible(false);
          setEditingSearches([]);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsEditFeesModalVisible(false);
              setEditingSearches([]);
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Cancel
          </Button>,
          <Button
            key="add"
            onClick={handleAddSearch}
            style={{
              backgroundColor: '#10B981',
              borderColor: '#10B981',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            + Add Search
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={updateLoading}
            onClick={handleSaveFees}
            style={{
              backgroundColor: '#5A1E66',
              borderColor: '#5A1E66',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            Save All Changes
          </Button>,
        ]}
        width={900}
      >
        <div className="space-y-4">
          <Alert
            type="info"
            message="Entry Fees Management"
            description="Add, edit, or remove searches and their entry fees. Each trial can have multiple searches with different fee structures."
            showIcon
          />

          {editingSearches.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No searches configured for this trial.</p>
              <Button
                type="primary"
                onClick={handleAddSearch}
                style={{
                  backgroundColor: '#5A1E66',
                  borderColor: '#5A1E66',
                }}
              >
                + Add First Search
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {editingSearches.map((search, index) => (
                <div key={search.key} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-semibold text-gray-700">Search #{index + 1}</h4>
                    <Button
                      danger
                      size="small"
                      onClick={() => handleRemoveSearch(search.key)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Level <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={search.level}
                        onChange={(value) => handleSearchChange(search.key, 'level', value)}
                        style={{ width: '100%' }}
                        placeholder="Select level"
                      >
                        <Select.Option value="SIT">SIT</Select.Option>
                        <Select.Option value="Novice">Novice</Select.Option>
                        <Select.Option value="Advanced">Advanced</Select.Option>
                        <Select.Option value="Excellent">Excellent</Select.Option>
                        <Select.Option value="Premier">Premier</Select.Option>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Element
                      </label>
                      <Input
                        value={search.element}
                        onChange={(e) => handleSearchChange(search.key, 'element', e.target.value)}
                        placeholder="e.g., Birch, Anise, Clove"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Search Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={search.searchName}
                        onChange={(e) => handleSearchChange(search.key, 'searchName', e.target.value)}
                        placeholder="e.g., Interior, Exterior, Container"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Entry Fee ($) <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        value={search.entryFee}
                        onChange={(e) => handleSearchChange(search.key, 'entryFee', e.target.value)}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

// Simple placeholder for awards (requires trial ID)
const SimplePlaceholder = ({ resource }) => {
  return (
    <div className="App rounded-md min-h-screen bg-white p-6 lg:p-10">
      <PageInfo title={`Database Admin - ${resource}`} />
      <div className="mt-6">
        <Alert
          message="Coming Soon"
          description={`The ${resource} admin interface will be implemented soon.`}
          type="info"
          showIcon
        />
      </div>
    </div>
  );
};

const menuItems = [
  // {
  //   key: 'users',
  //   icon: <UserOutlined className="h-6 w-6 mr-3" />,
  //   label: 'Users',
  //   path: '/admin/database/users',
  // },
  {
    key: 'members',
    icon: <TeamOutlined className="h-6 w-6 mr-3" />,
    label: 'Members',
    path: '/admin/database/members',
  },
  {
    key: 'dogs',
    icon: <TagsOutlined className="h-6 w-6 mr-3" />,
    label: 'Dogs',
    path: '/admin/database/dogs',
  },
  {
    key: 'trials',
    icon: <CalendarOutlined className="h-6 w-6 mr-3" />,
    label: 'Trials',
    path: '/admin/database/trials',
  },
  {
    key: 'trialResults',
    icon: <CheckCircleOutlined className="h-6 w-6 mr-3" />,
    label: 'Trial Results',
    path: '/admin/database/trialResults',
    disabled: true,
    comingSoon: true,
  },
  {
    key: 'payments',
    icon: <DollarOutlined className="h-6 w-6 mr-3" />,
    label: 'Payments',
    path: '/admin/database/payments',
  },
  {
    key: 'hosts',
    icon: <HomeOutlined className="h-6 w-6 mr-3" />,
    label: 'Hosts',
    path: '/admin/database/hosts',
    disabled: true,
    comingSoon: true,
  },
  {
    key: 'awards',
    icon: <TrophyOutlined className="h-6 w-6 mr-3" />,
    label: 'Awards',
    path: '/admin/database/awards',
    disabled: true,
    comingSoon: true,
  },
];

export const RefineAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useSelector((state) => state);
  const { profile, first_name, last_name } = auth?.memberReducer?.memberDetails || {};
  const role = useContext(RoleContext);

  // Get current selected key from URL
  // Extract the base path (e.g., 'members' from '/admin/database/members/123')
  const pathParts = location.pathname.split('/');
  const basePath = pathParts[3]; // Index 3 is the resource name after /admin/database/
  const selectedKey = menuItems.find(item => item.key === basePath)?.key || 'users';

  // Profile Component
  const ProfileComponent = () => {
    const [profileImg, setProfileImg] = useState(profile);

    useEffect(() => {
      setProfileImg(profile);
    }, [profile]);

    return (
      <>
        <img
          src={profileImg ? profileImg : avatar}
          alt=""
          className="h-10 w-10 mr-3 border rounded-[50px]"
          onError={() => {
            setProfileImg(avatar);
          }}
        />
        <div className="flex flex-col mr-6">
          <p className="text-sm font-semibold text-white w-36 break-all">{`${first_name || ''} ${last_name || ''}`}</p>
          <p className="text-sm font-regular text-white w-36 break-all">
            {role || 'Member'}
          </p>
        </div>
      </>
    );
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar with gradient background matching main app */}
      <div className="fixed bg-100 h-full text-white" style={{ width: "280px" }}>
        <div className="cursor-pointer bg-white py-4 ml-4 border-lt rounded-lg mr-[-10px]">
          <h1 className="text-primary text-xl font-bold ml-3">Database Admin</h1>
        </div>
        <div className="py-8 px-4 h-[72vh] overflow-y-scroll">
          <ul>
            {menuItems.map((item) => (
              <li
                key={item.key}
                className={`mb-1 py-2 px-3 h-10 ${
                  item.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer hover:bg-lightBlue"
                } text-white rounded-md font-semibold ${
                  selectedKey === item.key && !item.disabled ? "bg-lightBlue" : ""
                }`}
                onClick={() => !item.disabled && navigate(item.path)}
              >
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center">
                    {item.icon}
                    <span>{item.label}</span>
                  </div>
                  {item.comingSoon && (
                    <span className="text-xs text-gray-300 italic">(coming soon)</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Profile and Home Button at bottom */}
        <div className="absolute self-center flex flex-row bottom-8 pt-6 mx-4 px-2">
          <Link to="/dashboard/myProfile" className="flex">
            <ProfileComponent />
          </Link>
          <Link to="/dashboard/memberDashboard">
            <button
              type="button"
              className="ml-2 p-2 hover:bg-lightBlue rounded-md transition-colors"
              title="Return to Dashboard"
            >
              <HomeOutlined className="text-white text-xl" />
            </button>
          </Link>
        </div>
      </div>

      {/* Main content area with left margin for sidebar */}
      <div className="flex-1" style={{ marginLeft: "280px" }}>
        <Routes>
          <Route path="users" element={<UsersList />} />
          <Route path="users/:authId" element={<UserDetailView />} />
          <Route path="members" element={<MembersList />} />
          <Route path="members/:authId" element={<MemberDetailView />} />
          <Route path="dogs" element={<DogsList />} />
          <Route path="dogs/:id" element={<DogDetailView />} />
          <Route path="trials" element={<TrialsList />} />
          <Route path="trials/:id" element={<TrialDetailView />} />
          <Route path="trialResults" element={<TrialResultsList />} />
          <Route path="payments" element={<PaymentsList />} />
          <Route path="payments/:id" element={<PaymentDetailView />} />
          <Route path="hosts" element={<HostsList />} />
          <Route path="awards" element={<SimplePlaceholder resource="Awards" />} />
          <Route index element={<Navigate to="members" replace />} />
        </Routes>
      </div>
    </div>
  );
};

